import gsap from 'gsap';
import { EventEmitter } from '../../engine/core/EventEmitter';
import { sound, SoundId } from '../../engine/audio/sound';
import { DiceResultAnims, CashWinAnims, DiceEquippedAnims, ProfileSidebetAnims, BalanceBumpAnims } from '../../game/config/AnimationConfig';
import { getSidebetIconUrl } from './sidebetIcon';
import { flyoutPool } from './flyoutPool';
import type { MicMode } from '../../settings/SettingsStore';

export type PlayerProfileMode = 'betting' | 'shooting' | 'hi_lo';

export interface PlayerProfile {
  id: string;
  username: string;
  avatar?: string;
  crewTag?: string;
  badge?: string;
  balance?: number;
  bet?: number;
  isBanker?: boolean;
  isShooter?: boolean;
  /** Show equipped dice thumbnails on this player's profile */
  showDice?: boolean;
  /** Dice IDs the player has equipped (for dice gear display) */
  equippedDice?: string[];
  /** CDN base URL for dice thumbnails */
  cdnBase?: string;
  /** Dice roll result values (1-6) to display as icons. For point show 1, for trips/ceelo show 3. */
  diceResult?: number[];
}

/** Slot a profile card lives in — diff-based setPlayers tracks this per playerId. */
export type ProfileSlot = 'topLeft' | 'topRight' | 'bottomRow';

/** Data for a dice result update — values to display plus optional spawn position for flyout */
export interface DiceResultUpdate {
  values: number[];
  /** Screen position (CSS px relative to canvas) to spawn flyout icons from */
  screenPosition?: { x: number; y: number };
  /**
   * Optional outcome label rendered above the dice icons during the flyout
   * (e.g. "Natural 7!", "CEE-LO!", "Made the 6!"). Anchored at the spawn
   * point — does NOT travel with the dice — and fades out after the icons
   * land on the profile card. Omit / pass null to skip the label.
   */
  label?: string | null;
  /**
   * Outcome family of `label` — picks the stinger that plays when the label
   * pops (win / point-or-tie / loss). Omit / null for a silent label.
   */
  labelKind?: 'win' | 'point' | 'loss' | null;
}

export interface PlayerProfilesOverlayOptions {
  container: HTMLElement;
  /**
   * Returns the current server-clock estimate (epoch ms). Used by the
   * countdown ring tween to compute elapsed pct so the visual lands on the
   * server's authoritative `endsAt` regardless of phone-clock skew. If
   * omitted, falls back to local `Date.now()`.
   */
  serverNow?: () => number;
}

function formatBalance(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

/** Truncate username to max chars with ellipsis */
function truncateUsername(name: string, max = 10): string {
  if (name.length <= max) return name;
  return name.slice(0, max - 1) + '\u2026';
}

// SVG icons
const BANK_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="#FFD700"><path d="M16 2L2 10v3h28v-3L16 2zm0 3.3L25 10H7l9-4.7zM5 15v8h3v-8H5zm6 0v8h3v-8h-3zm6 0v8h3v-8h-3zm6 0v8h3v-8h-3zM2 25v3h28v-3H2z"/></svg>`;
// Neutral push/tie chip — gold circle, "TIE" wordmark. Distinct from the
// gold bank icon (different shape: round vs columned) and from win/loss
// (no green / no red).
const TIE_CHIP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="28" fill="#1A1A1F" stroke="#FFD700" stroke-width="3"/><text x="32" y="40" text-anchor="middle" font-family="'Bebas Neue', 'Oswald', sans-serif" font-size="22" font-weight="bold" fill="#FFD700" letter-spacing="2">TIE</text></svg>`;
const MIC_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>`;
// Per-opponent voice indicator + mute toggle. Speaker = audible (glows green
// while they talk); speaker-off = locally muted by you.
const SPEAKER_ON_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;
const SPEAKER_OFF_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>`;
const MENU_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="5" y1="7" x2="19" y2="7"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="5" y1="17" x2="19" y2="17"/></svg>`;
const CHAT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>`;

/** Mic press shorter than this (push mode) latches the mic open instead of muting on release. */
const TAP_LATCH_MS = 250;

interface ProfileEvents {
  'ptt:start': void;
  'ptt:stop': void;
  /** Mic tap wants the audio.micMode setting changed — main.ts owns the write. */
  'mic:mode_request': { mode: MicMode };
  'menu:open': void;
  /** Local player tapped the chat badge on their avatar — open the compose bar. */
  'chat:open': void;
  /** Local player tapped their own equipped-dice gear — open the in-game dice library. */
  'gear:tap': { playerId: string };
  /** An opponent's profile card was tapped — open the player card. */
  'profile:tap': { playerId: string; username: string; picture: string; crewTag?: string };
  /** Opponent's speaker icon tapped — toggle locally muting their voice. */
  'peer:mute_toggle': { playerId: string };
}

export class PlayerProfilesOverlay extends EventEmitter<ProfileEvents> {
  private container: HTMLElement;
  private root: HTMLElement;
  private topLeft: HTMLElement;
  private topRight: HTMLElement;
  private bottomRow: HTMLElement;
  private isVisible = false;
  private speakingPlayers = new Set<string>();
  /** Opponents the local player has muted — persists across card re-renders. */
  private mutedPeers = new Set<string>();
  private isPushToTalk = false;
  /**
   * Mirrors the audio.micMode setting — the SINGLE source of truth for open
   * mic. 'open' = mic broadcasts whenever the in-game overlay is up (the
   * latched pulse rides this). Tap gestures don't mutate it directly: they
   * emit mic:mode_request, main.ts writes the settings store, and the change
   * flows back through setMicMode — so the Settings screen always agrees
   * with the button.
   */
  private micMode: MicMode = 'push';
  /** Track displayed dice results per player to detect changes */
  private displayedResults = new Map<string, string>();
  /** Track displayed sidebet icons per player: playerId → (iconType → count) */
  private displayedSidebets = new Map<string, Map<string, number>>();
  /** Track current list of players in the "Betting" activity label (ordered) */
  private displayedBettingPlayers: string[] = [];
  /** Overlay for flyout dice icons */
  private flyoutContainer: HTMLElement;
  /** Active flyout timelines per player (kill on rapid updates) */
  private flyoutTimelines = new Map<string, gsap.core.Timeline>();
  /** Promises tracking dice-result flyouts during their PRE-fly
   *  animate-out phase (existing icons scaling down before the new
   *  flyout starts). flyoutTimelines is empty during this 0.2s window
   *  because the new timeline isn't created until the animate-out's
   *  onComplete; without this tracker, `waitForAllFlyouts()` returns
   *  immediately and downstream flyouts (rollWins money flyout) start
   *  before the dice flyout has even spawned. Resolved when the dice
   *  flyout's actual timeline onComplete fires. */
  private pendingFlyoutResolvers = new Set<Promise<void>>();
  /** Per-player timer ring DOM element (lazy-created on first show). */
  private timerRings = new Map<string, HTMLElement>();
  /** Per-player pulse-ripple DOM element (sibling layer behind the ring). */
  private timerPulses = new Map<string, HTMLElement>();
  /** Per-player active GSAP tween driving the conic-gradient progress. */
  private timerTweens = new Map<string, gsap.core.Tween>();
  /** Last timer state applied — short-circuits redundant work. */
  private lastTimerSig: string | null = null;
  private serverNow: () => number;

  constructor(options: PlayerProfilesOverlayOptions) {
    super();
    this.container = options.container;
    this.serverNow = options.serverNow ?? (() => Date.now());
    this.root = this.createElement();
    this.topLeft = this.root.querySelector('.profiles-top-left')!;
    this.topRight = this.root.querySelector('.profiles-top-right')!;
    this.bottomRow = this.root.querySelector('.profiles-bottom-left')!;
    this.flyoutContainer = document.createElement('div');
    this.flyoutContainer.className = 'dice-flyout-overlay';
    this.root.appendChild(this.flyoutContainer);
    this.injectStyles();

    // Tap an opponent's card → open the player card. Ignore the local card
    // (its menu/PTT controls own their own clicks) and the menu/PTT buttons.
    this.root.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.profile-card__menu-badge, .profile-card__mic-large, .profile-card__chat-badge, .profile-card__mic')) return;
      const cardEl = target.closest<HTMLElement>('.profile-card[data-player-id]');
      if (!cardEl) return;
      const playerId = cardEl.dataset.playerId!;
      if (playerId === this.lastLocalId) return; // no self-card
      const p = this.profilesById.get(playerId);
      this.emit('profile:tap', {
        playerId,
        username: p?.username ?? 'Player',
        picture: p?.avatar ?? '',
        crewTag: p?.crewTag,
      });
    });
  }

  private createElement(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'profiles-overlay';
    root.innerHTML = `
      <div class="profiles-top-grid">
        <div class="profiles-top-left"></div>
        <div class="profiles-top-right"></div>
        <div class="betting-activity-label" aria-hidden="true">
          <span class="betting-activity-label__text">BETTING</span>
          <div class="betting-activity-label__avatars"></div>
        </div>
      </div>
      <div class="profiles-bottom-left"></div>
    `;
    this.container.appendChild(root);
    return root;
  }

  private injectStyles(): void {
    if (document.getElementById('profiles-overlay-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'profiles-overlay-styles';
    styles.textContent = `
      .profiles-overlay {
        position: absolute;
        inset: 0;
        pointer-events: none;
        font-family: 'Rajdhani', sans-serif;
        z-index: 45;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .profiles-overlay.visible {
        opacity: 1;
      }

      /* ── Top Grid: 2-column layout ── */
      .profiles-top-grid {
        position: absolute;
        top: calc(0.75rem + env(safe-area-inset-top, 0px));
        left: 8px;
        right: 8px;
        min-height: 12rem;
        display: flex;
        justify-content: space-between;
        pointer-events: none;
      }

      .profiles-top-left,
      .profiles-top-right {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .profiles-top-left { align-items: flex-start; }
      .profiles-top-right { align-items: flex-end; }

      /* ── Betting Activity Label (anchored to bottom of profiles grid) ──
         Flat solid-black background to match the tutorial / instruction
         labels — but the pill border-radius is preserved (it reads as a
         distinct "now playing" badge that way). The avatar ring also keeps
         its gold styling. */
      .betting-activity-label {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%) scale(0.9);
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 10px 4px 12px;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 999px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      .betting-activity-label.visible {
        opacity: 1;
        transform: translateX(-50%) scale(1);
      }

      .betting-activity-label__text {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 13px;
        letter-spacing: 2px;
        color: #FFD700;
        text-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
        line-height: 1;
      }

      .betting-activity-label__avatars {
        display: flex;
        align-items: center;
        gap: -4px;
      }

      .betting-activity-label__avatar {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.8);
        border: 1.5px solid rgba(255, 215, 0, 0.8);
        box-shadow: 0 0 6px rgba(255, 215, 0, 0.35);
        display: grid;
        place-items: center;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 11px;
        letter-spacing: 0.5px;
        color: #FFFFFF;
        overflow: hidden;
        margin-left: -6px;
        transform-origin: center;
      }

      .betting-activity-label__avatar:first-child {
        margin-left: 0;
      }

      .betting-activity-label__avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      /* ── Bottom (local player) ── */
      .profiles-bottom-left {
        position: absolute;
        bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
        left: 8px;
        pointer-events: none;
      }

      /* ══════════════════════════════════════════
         PROFILE CARD — structure
         ══════════════════════════════════════════ */

      .profile-card {
        display: flex;
        flex-direction: column;
        gap: 1px;
        transition: all 0.3s ease;
        /* Scale the WHOLE card a bit — avatar, name, balance, dice gear,
         * sidebet chips, badges all grow proportionally. Using zoom (not
         * transform scale) so the layout box grows too: stacked opponent
         * cards don't overlap and the bottom/top anchors stay put. Tune
         * the factor here. */
        zoom: 1.2;
      }

      /* ── Main row: avatar + aside ── */
      .profile-card__main {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 4px;
      }

      .profile-card.side-right .profile-card__main {
        flex-direction: row-reverse;
      }

      /* ── Aside: dice result (top) + sidebets (bottom) ── */
      .profile-card__aside {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        gap: 2px;
        min-width: 0;
        min-height: 0;
      }

      /* ── Avatar Circle ── */
      .profile-card__circle {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.75);
        border: 2px solid rgba(255, 255, 255, 0.25);
        display: grid;
        place-items: center;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 16px;
        letter-spacing: 1px;
        color: #FFFFFF;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        position: relative;
        overflow: visible;
        flex-shrink: 0;
        transition: width 0.3s ease, height 0.3s ease;
      }

      .profile-card__circle-img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        overflow: hidden;
      }

      .profile-card__circle-img img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .profile-card.local .profile-card__circle {
        border-color: rgba(255, 215, 0, 0.7);
      }

      .profile-card.shooter .profile-card__circle {
        border-color: rgba(0, 245, 255, 0.7);
        box-shadow: 0 0 8px rgba(0, 245, 255, 0.3);
      }

      .profile-card.local.shooter .profile-card__circle {
        border-color: rgba(255, 215, 0, 0.7);
        box-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
      }

      /* ── Turn Timer Layers (udgpoker-style) ──
         Two stacked sibling layers inside .profile-card__circle:
           1. timer-pulse — radiating box-shadow ripple. Rate escalates
              with urgency: green→red→red-fast.
           2. timer-ring — full-circle conic-gradient disc that fills
              clockwise from 0% to 100% over the timer's duration.
         The avatar text/image sits underneath both with z-index:0. */
      /* Pulse rate, color, and ripple distance are continuous: applyTimerRing
         interpolates them via CSS vars on every frame so the ring intensity
         tracks the pct smoothly instead of stepping at fixed thresholds. The
         keyframe stays the same; only the duration / color vars change. */
      .profile-card__timer-pulse {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 50%;
        pointer-events: none;
        z-index: 0;
        --pulse-rate-ms: 1000ms;
        --pulse-color-on: rgba(255, 215, 0, 0.85);
        --pulse-color-off: rgba(255, 215, 0, 0);
        --pulse-spread: 20px;
        animation: pulse-urgent var(--pulse-rate-ms) infinite;
      }
      @keyframes pulse-urgent {
        0%   { box-shadow: var(--pulse-color-on)  0 0 0 0; }
        75%  { box-shadow: var(--pulse-color-off) 0 0 0 var(--pulse-spread); }
        100% { box-shadow: var(--pulse-color-off) 0 0 0 var(--pulse-spread); }
      }
      .profile-card__timer-ring {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 50%;
        pointer-events: none;
        z-index: 1;
        opacity: 0.55;
      }

      /* ── Bank Badge ── */
      .profile-card__bank-badge {
        position: absolute;
        top: -2px;
        right: -6px;
        width: 22px;
        height: 22px;
        background: rgba(0, 0, 0, 0.85);
        border-radius: 50%;
        border: 1.5px solid #FFD700;
        display: grid;
        place-items: center;
        z-index: 2;
      }

      .profile-card__bank-badge svg {
        width: 14px;
        height: 14px;
      }

      /* Right-side: mirror badge */
      .profile-card.side-right .profile-card__bank-badge {
        right: auto;
        left: -6px;
      }

      /* ── Mic / speaker icon (per-opponent: tap to mute) ── */
      .profile-card__mic {
        width: 22px;
        height: 22px;
        padding: 0;
        border: none;
        appearance: none;
        -webkit-appearance: none;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        pointer-events: auto;
        cursor: pointer;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: rgba(255, 255, 255, 0.35);
        flex-shrink: 0;
        transition: color 0.2s, background 0.2s, box-shadow 0.2s;
        position: absolute;
        bottom: -4px;
        right: -4px;
        /* Above the timer ring/pulse (z1/z0) like every other circle-attached
           control (bank/chat badge z2, gear z3, menu z4) — without this the
           turn timer's conic disc paints over the speaker. */
        z-index: 2;
      }

      .profile-card__mic svg {
        width: 13px;
        height: 13px;
      }

      /* Right-side: mirror mic */
      .profile-card.side-right .profile-card__mic {
        right: auto;
        left: -4px;
      }

      .profile-card.local .profile-card__mic {
        pointer-events: auto;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.5);
      }

      .profile-card.local .profile-card__mic:hover {
        color: rgba(255, 255, 255, 0.7);
        background: rgba(255, 255, 255, 0.1);
      }

      .profile-card__mic.speaking {
        color: #00FF88;
        background: rgba(0, 255, 136, 0.15);
        box-shadow: 0 0 6px rgba(0, 255, 136, 0.3);
      }

      .profile-card__mic.ptt-active {
        color: #FF3366;
        background: rgba(255, 51, 102, 0.2);
        box-shadow: 0 0 8px rgba(255, 51, 102, 0.35);
      }

      /* Locally muted opponent — red speaker-off, no speaking glow. */
      .profile-card__mic.muted {
        color: #FF3366;
        background: rgba(255, 51, 102, 0.18);
        box-shadow: 0 0 6px rgba(255, 51, 102, 0.3);
      }

      .profile-card__mic:active {
        transform: scale(0.92);
      }

      /* ── Local-only: chunky standalone mic stacked above the profile ── */
      .profile-card__mic-large {
        order: -1;
        align-self: flex-start;
        margin-bottom: 8px;
        width: 56px;
        height: 56px;
        padding: 0;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.85);
        color: rgba(255, 255, 255, 0.75);
        display: grid;
        place-items: center;
        cursor: pointer;
        pointer-events: auto;
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        user-select: none;
        transition: color 0.15s, background 0.15s, box-shadow 0.15s, transform 0.08s;
        will-change: transform;
      }

      .profile-card__mic-large svg {
        width: 28px;
        height: 28px;
      }

      .profile-card__mic-large:active {
        transform: scale(0.94);
      }

      .profile-card__mic-large.speaking {
        color: #00FF88;
        background: rgba(0, 255, 136, 0.18);
        box-shadow: 0 0 12px rgba(0, 255, 136, 0.45);
      }

      .profile-card__mic-large.ptt-active {
        color: #FF3366;
        background: rgba(255, 51, 102, 0.25);
        box-shadow: 0 0 14px rgba(255, 51, 102, 0.5);
      }

      /* Latched OPEN (tap-latch in push mode, or open-mic mode): same hot red
         as a held press plus a slow live pulse, so a mic left on stays
         conspicuous. Infinite loop on an already-visible element, so the
         first-frame flicker rule doesn't apply. Declared after .speaking and
         .ptt-active so the steady red wins while latched instead of strobing
         green with voice activity. */
      .profile-card__mic-large.mic-latched {
        color: #FF3366;
        background: rgba(255, 51, 102, 0.25);
        border-color: rgba(255, 51, 102, 0.75);
        animation: sdl-mic-live-pulse 1.6s ease-in-out infinite;
      }

      @keyframes sdl-mic-live-pulse {
        0%, 100% { box-shadow: 0 0 10px rgba(255, 51, 102, 0.4); }
        50% { box-shadow: 0 0 22px rgba(255, 51, 102, 0.85); }
      }

      /* ── Local-only: menu hamburger badge at top-LEFT of avatar circle ──
         Sits opposite the bank badge (top-right) so a banker who is also the
         local player doesn't end up with two badges fighting for the same
         pixel. Background uses a subtle radial gradient + cyan rim glow to
         match the urban-neon aesthetic without screaming for attention. */
      .profile-card__menu-badge {
        position: absolute;
        top: -5px;
        left: -5px;
        width: 24px;
        height: 24px;
        padding: 0;
        background: radial-gradient(circle at 30% 25%, #2a2a2a 0%, #0a0a0a 80%);
        border: 1.5px solid rgba(79, 195, 247, 0.55);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.95);
        display: grid;
        place-items: center;
        cursor: pointer;
        pointer-events: auto;
        /* Above the dice gear (z3) and bank badge (z2) so an overlap in any
           layout can never steal the tap from the menu / Leave-Match control. */
        z-index: 4;
        box-shadow:
          0 2px 6px rgba(0, 0, 0, 0.5),
          0 0 8px rgba(79, 195, 247, 0.25);
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        user-select: none;
        transition: transform 0.08s, box-shadow 0.15s, border-color 0.15s;
      }

      /* Enlarge the touch target to ~40px (the 24px badge is a small tap on
         phones) without changing how it looks. The extension only covers
         non-interactive avatar space at the top-left corner, so it can't steal
         taps from the gear (bottom) or bank badge (top-right). */
      .profile-card__menu-badge::after {
        content: '';
        position: absolute;
        inset: -8px;
      }

      .profile-card__menu-badge svg {
        width: 14px;
        height: 14px;
      }

      .profile-card__menu-badge:hover {
        border-color: rgba(79, 195, 247, 0.85);
        box-shadow:
          0 2px 6px rgba(0, 0, 0, 0.5),
          0 0 12px rgba(79, 195, 247, 0.45);
      }

      .profile-card__menu-badge:active {
        transform: scale(0.9);
      }

      /* Chat badge — mirrors the menu badge but anchored bottom-right of the
         avatar, green accent. Sits out of the throw/gesture zone. */
      .profile-card__chat-badge {
        position: absolute;
        bottom: -5px;
        right: -5px;
        width: 24px;
        height: 24px;
        padding: 0;
        background: radial-gradient(circle at 30% 25%, #2a2a2a 0%, #0a0a0a 80%);
        border: 1.5px solid rgba(0, 255, 136, 0.55);
        border-radius: 50%;
        color: rgba(255, 255, 255, 0.95);
        display: grid;
        place-items: center;
        cursor: pointer;
        pointer-events: auto;
        z-index: 2;
        box-shadow:
          0 2px 6px rgba(0, 0, 0, 0.5),
          0 0 8px rgba(0, 255, 136, 0.25);
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
        user-select: none;
        transition: transform 0.08s, box-shadow 0.15s, border-color 0.15s;
      }
      .profile-card__chat-badge svg { width: 13px; height: 13px; }
      .profile-card__chat-badge:hover {
        border-color: rgba(0, 255, 136, 0.85);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5), 0 0 12px rgba(0, 255, 136, 0.45);
      }
      .profile-card__chat-badge:active { transform: scale(0.9); }
      /* Unread nudge — soft pulse when a new message landed and the bar is closed. */
      .profile-card__chat-badge.has-unread {
        border-color: rgba(0, 255, 136, 0.95);
        animation: chat-badge-pulse 1.4s ease-in-out infinite;
      }
      @keyframes chat-badge-pulse {
        0%, 100% { box-shadow: 0 2px 6px rgba(0,0,0,0.5), 0 0 8px rgba(0,255,136,0.3); }
        50% { box-shadow: 0 2px 6px rgba(0,0,0,0.5), 0 0 16px rgba(0,255,136,0.7); }
      }

      /* ── Info (name + balance) ── */
      .profile-card__info {
        line-height: 1.1;
      }

      .profile-card:not(.side-right) .profile-card__info { text-align: left; }
      .profile-card.side-right .profile-card__info { text-align: right; }

      .profile-card.side-right .profile-card__name,
      .profile-card.side-right .profile-card__balance {
        margin-left: auto;
      }

      .profile-card__name {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: rgba(255, 255, 255, 0.95);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 90px;
        text-shadow: 0 0 4px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0, 0, 0, 0.8);
        background: black;
        padding: 0 0.3rem;
        border-radius: 0.5rem;
        width: fit-content;
      }

      .profile-card.local .profile-card__name {
        color: #FFD700;
      }

      .profile-card__balance {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 700;
        font-size: 13px;
        color: #00FF88;
        text-shadow: 0 0 4px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0, 0, 0, 0.8);
        background: black;
        padding: 0 0.3rem;
        border-radius: 0.5rem;
        width: fit-content;
      }

      /* ── Dice Result Icons ── */
      .profile-card__dice-result {
        display: flex;
        gap: 2px;
        align-items: center;
        flex: 1 1 0;
        min-height: 0;
        padding: 0.1rem;
      }

      .profile-card__dice-result img {
        max-height: 100%;
        height: 100%;
        width: auto;
        object-fit: contain;
        display: block;
      }

      .profile-card.side-right .profile-card__dice-result {
        justify-content: flex-end;
      }

      /* ── Sidebets Container ── */
      .profile-card__bets {
        display: flex;
        gap: 3px;
        align-items: center;
        flex: 1 1 0;
        min-height: 0;
        overflow: visible;
        padding: 0.1rem 0.1rem 0.3rem;
      }

      .profile-card.side-right .profile-card__bets {
        justify-content: flex-end;
      }

      .profile-card__sidebet {
        position: relative;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        transform-origin: center;
      }

      .profile-card__sidebet img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
      }

      .profile-card__sidebet-count {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 14px;
        height: 14px;
        padding: 0 3px;
        border-radius: 7px;
        background: #FF3366;
        color: #FFFFFF;
        font-family: 'Rajdhani', sans-serif;
        font-size: 10px;
        font-weight: 700;
        line-height: 14px;
        text-align: center;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
        box-sizing: border-box;
        transform-origin: center;
      }

      /* ── Dice Gear ── */
      .profile-card__dice-gear {
        position: absolute;
        bottom: 0;
        left: -8px;
        z-index: 3;
        filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.85));
        transform: scale(0.85);
        transform-origin: center;
        transition: transform 0.3s ease;
      }

      /* Local player's gear is interactive — opens the in-game dice library.
         The base container is 0x0 (all thumbs are absolutely positioned), so
         give it a real box for a reliable tap target. The thumbs are anchored
         to the container's bottom edge, which stays put, so sizing it does not
         shift them. */
      .profile-card__dice-gear--tappable {
        pointer-events: auto;
        cursor: pointer;
        width: 34px;
        height: 34px;
      }
      .profile-card.local .profile-card__dice-gear--tappable:active {
        transform: scale(0.92);
      }

      /* Right-side: mirror dice gear */
      .profile-card.side-right .profile-card__dice-gear {
        left: auto;
        right: -8px;
      }

      .profile-card__dice-thumb {
        width: 18px;
        height: 18px;
        position: absolute;
        border-radius: 3px;
        overflow: hidden;
        transform-origin: center;
      }

      .profile-card__dice-thumb:nth-child(1) { bottom: 0; left: 0; }
      .profile-card__dice-thumb:nth-child(2) { bottom: 9px; left: 5px; }
      .profile-card__dice-thumb:nth-child(3) { bottom: -4px; left: 10px; }

      /* Right-side: mirror thumb offsets */
      .profile-card.side-right .profile-card__dice-thumb:nth-child(1) { left: auto; right: 0; }
      .profile-card.side-right .profile-card__dice-thumb:nth-child(2) { left: auto; right: 5px; }
      .profile-card.side-right .profile-card__dice-thumb:nth-child(3) { left: auto; right: 10px; }

      .profile-card__dice-thumb img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      /* ══════════════════════════════════════════
         LOCAL (bottom) — always large; opponents (top) stay small
         ══════════════════════════════════════════ */

      .profile-card.local .profile-card__circle {
        width: 64px;
        height: 64px;
        font-size: 20px;
      }

      .profile-card.local .profile-card__dice-gear {
        transform: scale(1);
      }

      .profile-card.local .profile-card__bank-badge {
        width: 26px;
        height: 26px;
      }

      .profile-card.local .profile-card__bank-badge svg {
        width: 17px;
        height: 17px;
      }

      /* ── Dice Flyout Overlay ── */
      .dice-flyout-overlay {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 46;
        overflow: visible;
      }
      .dice-flyout-group {
        position: absolute;
        display: flex;
        gap: 4px;
        transform-origin: center;
        will-change: transform, left, top, opacity;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.6));
      }
      .dice-flyout-group img {
        height: 32px;
        width: auto;
        display: block;
      }
      .dice-flyout-group.flyout-trail {
        opacity: 0.3;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3)) blur(2px);
      }
      .dice-flyout-label {
        position: absolute;
        font-family: 'Bebas Neue', 'Oswald', sans-serif;
        font-size: 28px;
        letter-spacing: 0.04em;
        color: #FFFFFF;
        text-shadow:
          0 0 12px rgba(255, 215, 0, 0.55),
          0 2px 6px rgba(0, 0, 0, 0.85);
        white-space: nowrap;
        pointer-events: none;
        transform-origin: center;
        will-change: transform, opacity;
        z-index: 47;
      }

      /* ── Cash Win Flyout ── */
      .cash-flyout-note {
        position: absolute;
        width: ${CashWinAnims.noteSize}px;
        height: auto;
        transform-origin: center;
        will-change: transform, left, top, opacity;
        filter: drop-shadow(0 2px 10px rgba(0, 0, 0, 0.7));
        pointer-events: none;
      }
      .cash-flyout-note.flyout-trail {
        opacity: 0.25;
        filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3)) blur(2px);
      }
      .cash-win-amount {
        position: absolute;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 20px;
        color: #44FF44;
        text-shadow: 0 0 8px rgba(68, 255, 68, 0.6), 0 2px 4px rgba(0, 0, 0, 0.8);
        white-space: nowrap;
        pointer-events: none;
        transform-origin: center;
        will-change: transform, opacity;
        z-index: 48;
      }

      /* ── HI_LO Mode ── */
      .profiles-overlay.hi-lo .profile-card__dice-result {
        height: 100%;
        padding: 0.5rem 0;
      }
      .profiles-overlay.hi-lo .profile-card__bets {
        display: none;
      }

      /* ── Solo grind: no one to talk to → hide local mic + chat ── */
      .profiles-overlay.social-off .profile-card__mic-large,
      .profiles-overlay.social-off .profile-card__chat-badge {
        display: none !important;
      }
    `;
    document.head.appendChild(styles);
  }

  show(): void {
    this.isVisible = true;
    this.root.classList.add('visible');
    // Open mic re-arms whenever the in-game overlay comes (back) up — fresh
    // match, state re-entry. Idempotent at the VoiceClient, so firing from
    // every state onEnter is free.
    this.reassertMicHot();
  }

  hide(): void {
    this.isVisible = false;
    this.root.classList.remove('visible');
    // The mic button leaves the screen with the overlay — the mic must never
    // keep broadcasting past it (ptt:stop → VoiceClient mute). The open-mic
    // PREFERENCE survives; show() re-arms it next match.
    this.muteMic();
  }

  /**
   * Temporarily lift the overlay above the home UI (nav z:100, modals z:501)
   * so a cash flyout aimed at a home-screen element renders on top instead of
   * behind it. Restored by passing false. Default CSS z-index is 45 (in-game,
   * over the 3D scene).
   */
  setFlyoutAboveUI(on: boolean): void {
    this.root.style.zIndex = on ? '700' : '';
  }

  setMode(mode: PlayerProfileMode): void {
    // Size is per-card now: local (bottom) = large, opponents (top) = small.
    // Mode only toggles hi-lo layout variant.
    this.root.classList.toggle('hi-lo', mode === 'hi_lo');
  }

  /**
   * Toggle the social affordances (local mic + chat badge). Solo grind has no
   * one to talk to, so both are hidden there. Driven per-update off gameType so
   * it always resets to visible when a real cee-lo / craps table loads.
   */
  setSocialEnabled(enabled: boolean): void {
    this.root.classList.toggle('social-off', !enabled);
  }

  /** Hide all banker badges across all player cards */
  hideBankerBadge(): void {
    this.root.querySelectorAll('.profile-card__bank-badge').forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });
  }

  /** Hide the banker badge on a single player's card. */
  hideBankerBadgeFor(playerId: string): void {
    const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
    const badge = card?.querySelector('.profile-card__bank-badge') as HTMLElement | null;
    if (badge) badge.style.display = 'none';
  }

  /**
   * Center-of-bank-badge in flyout-overlay coordinates for a player, or
   * null if the player's profile / badge can't be located. Used as the
   * source point for inter-shooter bank-icon flyouts in craps.
   */
  getBankBadgeScreenPos(playerId: string): { x: number; y: number } | null {
    const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
    const badge = card?.querySelector('.profile-card__bank-badge') as HTMLElement | null;
    if (!badge) return null;
    const wasHidden = badge.style.display === 'none';
    if (wasHidden) badge.style.display = '';
    const rect = this._visualRect(badge); // zoom-corrected (WebKit)
    if (wasHidden) badge.style.display = 'none';
    if (!rect.width && !rect.height) {
      // Fallback to the profile circle center if badge isn't laid out.
      const circle = card?.querySelector('.profile-card__circle');
      if (!circle) return null;
      const cr = this._visualRect(circle as HTMLElement); // zoom-corrected (WebKit)
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      return {
        x: cr.left - overlayRect.left + cr.width / 2,
        y: cr.top - overlayRect.top + cr.height / 2,
      };
    }
    const overlayRect = this.flyoutContainer.getBoundingClientRect();
    return {
      x: rect.left - overlayRect.left + rect.width / 2,
      y: rect.top - overlayRect.top + rect.height / 2,
    };
  }

  /** Does getBoundingClientRect bake in CSS `zoom`? Chrome/Blink: yes. WebKit/iOS:
   *  no (returns the rect as if zoom were 1). Detected once via a 2× probe. */
  private static _zoomInRect: boolean | null = null;
  private _zoomCountedInRect(): boolean {
    if (PlayerProfilesOverlay._zoomInRect !== null) return PlayerProfilesOverlay._zoomInRect;
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;left:0;top:0;width:100px;height:100px;zoom:2;visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    PlayerProfilesOverlay._zoomInRect = probe.getBoundingClientRect().width > 150;
    probe.remove();
    return PlayerProfilesOverlay._zoomInRect;
  }

  /** Cumulative CSS `zoom` of an element and its ancestors (1 if none). */
  private _cumulativeZoom(el: HTMLElement | null): number {
    let z = 1;
    for (let n: HTMLElement | null = el; n; n = n.parentElement) {
      const v = parseFloat(getComputedStyle(n).zoom);
      if (!isNaN(v) && v > 0) z *= v;
    }
    return z;
  }

  /**
   * Viewport rect of an element, corrected for the WebKit `zoom` +
   * getBoundingClientRect mismatch. On Chrome the raw rect is already visual; on
   * WebKit (iOS) the rect ignores `zoom`, so we scale it by the element's
   * cumulative zoom to get the real on-screen position. This is what makes
   * flyouts to the zoom:1.2 profile cards land correctly on iPhone.
   */
  private _visualRect(el: HTMLElement): { left: number; top: number; width: number; height: number } {
    const r = el.getBoundingClientRect();
    if (this._zoomCountedInRect()) return r;
    const z = this._cumulativeZoom(el);
    if (z === 1) return r;
    return { left: r.left * z, top: r.top * z, width: r.width * z, height: r.height * z };
  }

  /**
   * Animate all players' dice gear thumbnails to scale 0 (individual
   * dice, staggered). Pass `excludePlayerId` to leave one player's
   * gear at scale 1 — used at HI_LO exit and post-roll shooter handoff
   * so the new banker / shooter's gear stays visible (no disappear-
   * then-reappear flicker).
   */
  animateDiceGearOut(duration: number, excludePlayerId?: string | null): Promise<void> {
    return new Promise((resolve) => {
      const selector = excludePlayerId
        ? `.profile-card:not([data-player-id="${excludePlayerId}"]) .profile-card__dice-thumb`
        : '.profile-card__dice-thumb';
      const thumbs = Array.from(this.root.querySelectorAll(selector));
      if (thumbs.length === 0) { resolve(); return; }
      gsap.to(thumbs, {
        scale: 0,
        duration,
        ease: 'back.in(2)',
        stagger: 0.04,
        onComplete: resolve,
      });
    });
  }

  /** Animate dice gear back in on the winning player (individual dice, staggered) */
  revealWinner(playerId: string, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      if (!card) { resolve(); return; }

      const thumbs = Array.from(card.querySelectorAll('.profile-card__dice-thumb'));
      if (thumbs.length > 0) {
        gsap.to(thumbs, {
          scale: 1,
          duration,
          ease: 'back.out(1.7)',
          stagger: 0.06,
          onComplete: resolve,
        });
      } else {
        resolve();
      }
    });
  }

  /** Number of active flyout timelines */
  getActiveFlyoutCount(): number {
    return this.flyoutTimelines.size;
  }

  /** Wait for ALL active dice result flyout animations to complete,
   *  including those still in their pre-fly animate-out phase (tracked
   *  via pendingFlyoutResolvers) — see the field doc for why. */
  waitForAllFlyouts(): Promise<void> {
    if (this.flyoutTimelines.size === 0 && this.pendingFlyoutResolvers.size === 0) {
      return Promise.resolve();
    }
    const promises: Promise<void>[] = [];
    for (const [, tl] of this.flyoutTimelines) {
      promises.push(new Promise((resolve) => {
        const existing = tl.eventCallback('onComplete') as (() => void) | null;
        tl.eventCallback('onComplete', () => {
          existing?.();
          resolve();
        });
      }));
    }
    for (const p of this.pendingFlyoutResolvers) {
      promises.push(p);
    }
    return Promise.all(promises).then(() => {});
  }

  /**
   * Flyout bank icon from a screen position to the winner's profile badge.
   * Creates a temp icon at screenPos, arc-flies to badge location, then shows the real badge.
   */
  flyoutBankIcon(playerId: string, screenPos: { x: number; y: number }, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      if (!card) { resolve(); return; }

      // Find or create the badge target position
      const circle = card.querySelector('.profile-card__circle');
      if (!circle) { resolve(); return; }

      // Ensure badge exists but hidden
      let badge = card.querySelector('.profile-card__bank-badge') as HTMLElement | null;
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'profile-card__bank-badge';
        badge.innerHTML = BANK_ICON_SVG;
        circle.appendChild(badge);
      }
      badge.style.display = 'none';

      // Get badge target position relative to flyout overlay
      // Temporarily show to measure, then re-hide
      badge.style.display = '';
      badge.style.visibility = 'hidden';
      const badgeRect = this._visualRect(badge); // zoom-corrected (WebKit)
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      // Source into overlay-local coords (matches the dest below). Off by
      // overlayRect.top otherwise — ~0 desktop, ~50px iPhone safe-area.
      screenPos = { x: screenPos.x - overlayRect.left, y: screenPos.y - overlayRect.top };
      const endX = badgeRect.left - overlayRect.left + badgeRect.width / 2;
      const endY = badgeRect.top - overlayRect.top + badgeRect.height / 2;
      badge.style.display = 'none';
      badge.style.visibility = '';

      // Flyout bank icon — pooled.
      const flyIcon = flyoutPool.acquire('bank-fly', () => {
        const el = document.createElement('div');
        el.className = 'profile-card__bank-badge';
        el.innerHTML = BANK_ICON_SVG;
        el.style.position = 'absolute';
        el.style.zIndex = '47';
        return el;
      });
      flyIcon.style.left = `${screenPos.x}px`;
      flyIcon.style.top = `${screenPos.y}px`;
      flyIcon.style.transform = 'translate(-50%, -50%) scale(0)';
      this.flyoutContainer.appendChild(flyIcon);

      const PEAK_SCALE = 2.5;
      const PEAK_HOLD = 0.2; // Brief hold at peak so the winner reveal reads distinctly from the dice-result flyout
      const startX = screenPos.x;
      const startY = screenPos.y;
      const cpX = (startX + endX) / 2;
      const cpY = Math.min(startY, endY) - 40;

      // Bezier helper
      const bezier = (a: number, cp: number, b: number, t: number) => {
        const inv = 1 - t;
        return inv * inv * a + 2 * inv * t * cp + t * t * b;
      };

      // Create trail ghosts
      const TRAIL_COUNT = 2;
      const TRAIL_DELAY = 0.04;
      const trailEls: HTMLElement[] = [];
      for (let t = 0; t < TRAIL_COUNT; t++) {
        const trail = flyoutPool.acquire('bank-trail', () => {
          const el = document.createElement('div');
          el.className = 'profile-card__bank-badge flyout-trail';
          el.innerHTML = BANK_ICON_SVG;
          el.style.position = 'absolute';
          el.style.zIndex = '46';
          return el;
        });
        trail.style.left = `${startX}px`;
        trail.style.top = `${startY}px`;
        trail.style.transform = 'translate(-50%, -50%) scale(0)';
        this.flyoutContainer.appendChild(trail);
        trailEls.push(trail);
      }

      const tl = gsap.timeline({
        onComplete: () => {
          flyoutPool.release(flyIcon);
          flyoutPool.releaseAll(trailEls);
          // Show real badge with pop
          if (badge) {
            badge.style.display = '';
            gsap.fromTo(badge, { scale: 0 }, { scale: 1, duration: 0.25, ease: 'back.out(1.7)' });
          }
          resolve();
        },
      });

      // Phase 1: Scale in at dice position (bigger pop — clearly different silhouette vs dice-result flyout)
      const scaleInDuration = duration * 0.35;
      tl.to(flyIcon, {
        duration: scaleInDuration,
        ease: 'back.out(1.7)',
        scale: PEAK_SCALE,
        onUpdate() {
          const s = gsap.getProperty(flyIcon, 'scale') as number;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      });

      // Phase 1b: Hold at peak so the moment lands. A gentle pulse keeps it alive.
      tl.to(flyIcon, {
        duration: PEAK_HOLD,
        scale: PEAK_SCALE * 1.05,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
        onUpdate() {
          const s = gsap.getProperty(flyIcon, 'scale') as number;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      });

      // Phase 2: Arc-fly to badge position with fade + trails
      const flyDuration = duration * 0.65;
      const flyOffset = scaleInDuration + PEAK_HOLD * 2; // after scale-in + hold (yoyo doubles)

      const flyerProxy = { t: 0 };
      tl.to(flyerProxy, {
        t: 1,
        duration: flyDuration,
        ease: 'power2.inOut',
        onUpdate: () => {
          const t = flyerProxy.t;
          const px = bezier(startX, cpX, endX, t);
          const py = bezier(startY, cpY, endY, t);
          const s = PEAK_SCALE - (PEAK_SCALE - 1) * t;
          const opacity = t < 0.85 ? 1 : 1 - (t - 0.85) / 0.15;
          flyIcon.style.left = `${px}px`;
          flyIcon.style.top = `${py}px`;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
          flyIcon.style.opacity = String(opacity);
        },
      }, flyOffset);

      // Trail ghosts follow with delay
      trailEls.forEach((trail, tIdx) => {
        const trailProxy = { t: 0 };
        tl.to(trailProxy, {
          t: 1,
          duration: flyDuration,
          ease: 'power2.inOut',
          onUpdate: () => {
            const t = trailProxy.t;
            const px = bezier(startX, cpX, endX, t);
            const py = bezier(startY, cpY, endY, t);
            const s = (PEAK_SCALE - (PEAK_SCALE - 1) * t) * 0.85;
            const baseOpacity = 0.3 - tIdx * 0.1;
            const opacity = t < 0.85 ? baseOpacity : baseOpacity * (1 - (t - 0.85) / 0.15);
            trail.style.left = `${px}px`;
            trail.style.top = `${py}px`;
            trail.style.transform = `translate(-50%, -50%) scale(${s})`;
            trail.style.opacity = String(Math.max(0, opacity));
          },
        }, flyOffset + (tIdx + 1) * TRAIL_DELAY);
      });
    });
  }

  /**
   * Spawn a TIE chip at `screenPos` and arc-fly it to the given player's
   * profile card. Resolves when the flyout settles. Unlike `flyoutBankIcon`,
   * no persistent badge is created — the chip just lands on the card with a
   * brief pulse and then fades. Duration is the total animation time.
   */
  flyoutTieChip(playerId: string, screenPos: { x: number; y: number }, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      if (!card) { resolve(); return; }
      const circle = card.querySelector('.profile-card__circle');
      if (!circle) { resolve(); return; }

      // Target = center of the player's profile circle.
      const circleRect = this._visualRect(circle as HTMLElement); // zoom-corrected (WebKit)
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      // Source into overlay-local coords (matches the dest). Off by overlayRect.top
      // otherwise — ~0 desktop, ~50px iPhone safe-area.
      screenPos = { x: screenPos.x - overlayRect.left, y: screenPos.y - overlayRect.top };
      const endX = circleRect.left - overlayRect.left + circleRect.width / 2;
      const endY = circleRect.top - overlayRect.top + circleRect.height / 2;

      // Tie chip — pooled. Match the bank flyout icon footprint exactly: the
      // bank flyer is a 22px `.profile-card__bank-badge` peaking at 2.5×. Same
      // base + peak so both showdown flyouts read at the same scale.
      const flyIcon = flyoutPool.acquire('tie-chip', () => {
        const el = document.createElement('div');
        el.className = 'flyout-tie-chip';
        el.innerHTML = TIE_CHIP_SVG;
        el.style.position = 'absolute';
        el.style.width = '22px';
        el.style.height = '22px';
        el.style.zIndex = '47';
        el.style.pointerEvents = 'none';
        return el;
      });
      flyIcon.style.left = `${screenPos.x}px`;
      flyIcon.style.top = `${screenPos.y}px`;
      flyIcon.style.transform = 'translate(-50%, -50%) scale(0)';
      this.flyoutContainer.appendChild(flyIcon);

      const PEAK_SCALE = 2.5;
      const PEAK_HOLD = 0.18;
      const startX = screenPos.x;
      const startY = screenPos.y;
      const cpX = (startX + endX) / 2;
      const cpY = Math.min(startY, endY) - 40;
      const bezier = (a: number, cp: number, b: number, t: number) => {
        const inv = 1 - t;
        return inv * inv * a + 2 * inv * t * cp + t * t * b;
      };

      const tl = gsap.timeline({
        onComplete: () => {
          flyoutPool.release(flyIcon);
          resolve();
        },
      });

      // Phase 1: scale-in pop
      const scaleInDuration = duration * 0.3;
      tl.to(flyIcon, {
        duration: scaleInDuration,
        ease: 'back.out(1.7)',
        scale: PEAK_SCALE,
        onUpdate() {
          const s = gsap.getProperty(flyIcon, 'scale') as number;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      });

      // Phase 1b: brief hold so the player can read "TIE"
      tl.to(flyIcon, {
        duration: PEAK_HOLD,
        scale: PEAK_SCALE * 1.05,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
        onUpdate() {
          const s = gsap.getProperty(flyIcon, 'scale') as number;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      });

      // Phase 2: arc-fly to profile card, scaling down and fading at the end
      const flyDuration = duration * 0.55;
      const flyOffset = scaleInDuration + PEAK_HOLD * 2;
      const flyerProxy = { t: 0 };
      tl.to(flyerProxy, {
        t: 1,
        duration: flyDuration,
        ease: 'power2.inOut',
        onUpdate: () => {
          const t = flyerProxy.t;
          const px = bezier(startX, cpX, endX, t);
          const py = bezier(startY, cpY, endY, t);
          const s = PEAK_SCALE - (PEAK_SCALE - 0.6) * t;
          const opacity = t < 0.85 ? 1 : 1 - (t - 0.85) / 0.15;
          flyIcon.style.left = `${px}px`;
          flyIcon.style.top = `${py}px`;
          flyIcon.style.transform = `translate(-50%, -50%) scale(${s})`;
          flyIcon.style.opacity = String(opacity);
        },
      }, flyOffset);
    });
  }

  /**
   * Sync the profile-card timer rings to the server's authoritative timer.
   *
   * @param activePlayerIds  Players whose card should show the ring.
   * @param timer            Current timer slot from `GameUpdate.timer`.
   *                         `null` or `visible: false` → all rings hidden.
   *                         `visible: true` → ring runs from current
   *                         elapsed-pct to 100% over `endsAt - Date.now()`.
   *
   * Idempotent — if the timer state hasn't changed since the last call,
   * returns immediately to avoid restarting in-flight tweens.
   */
  setProfileTimer(
    activePlayerIds: string[],
    timer: { id: string; startedAt: number; endsAt: number; visible: boolean } | null,
  ): void {
    // Pre-timer / no timer: clear all rings.
    if (!timer || !timer.visible) {
      this.clearAllProfileTimers();
      this.lastTimerSig = null;
      return;
    }

    // Cheap signature so we don't kill+restart the tween on identical updates.
    // Includes endsAt + sorted player set + timer id; if any change, reapply.
    const sortedIds = [...activePlayerIds].sort();
    const sig = `${timer.id}|${timer.startedAt}|${timer.endsAt}|${sortedIds.join(',')}`;
    if (sig === this.lastTimerSig) return;
    this.lastTimerSig = sig;

    // Drop rings on cards that are no longer active.
    const activeSet = new Set(activePlayerIds);
    for (const pid of [...this.timerRings.keys()]) {
      if (!activeSet.has(pid)) this.removeTimerRing(pid);
    }

    // Apply / refresh rings on active cards.
    for (const playerId of activePlayerIds) {
      this.applyTimerRing(playerId, timer.startedAt, timer.endsAt);
    }
  }

  private clearAllProfileTimers(): void {
    for (const pid of [...this.timerRings.keys()]) {
      this.removeTimerRing(pid);
    }
  }

  private removeTimerRing(playerId: string): void {
    const tween = this.timerTweens.get(playerId);
    if (tween) {
      tween.kill();
      this.timerTweens.delete(playerId);
    }
    // Fade ring + pulse out over 180ms instead of yanking them from the DOM.
    // A hard remove between phases reads as a "blink"; a fade lets the eye
    // track the timer ending naturally. Refs are cleared synchronously so a
    // re-apply on the same card during the fade re-creates fresh elements.
    const ring = this.timerRings.get(playerId);
    if (ring) {
      this.timerRings.delete(playerId);
      gsap.killTweensOf(ring);
      gsap.to(ring, {
        opacity: 0,
        duration: 0.18,
        ease: 'power1.out',
        onComplete: () => ring.remove(),
      });
    }
    const pulse = this.timerPulses.get(playerId);
    if (pulse) {
      this.timerPulses.delete(playerId);
      gsap.killTweensOf(pulse);
      gsap.to(pulse, {
        opacity: 0,
        duration: 0.18,
        ease: 'power1.out',
        onComplete: () => pulse.remove(),
      });
    }
  }

  private applyTimerRing(playerId: string, startedAt: number, endsAt: number): void {
    const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
    if (!card) return;
    const circle = card.querySelector('.profile-card__circle');
    if (!circle) return;

    // Pulse layer — single keyframe driven by CSS vars so rate/colour change
    // continuously with pct (no class-flip steps).
    //
    // If a fade-out tween is mid-flight on this card's old pulse element
    // (phase A exiting → phase B entering on the same player within 180ms),
    // recover that element rather than letting the fade strip it from the
    // DOM and double up. Same for the ring below.
    const existingPulse = circle.querySelector<HTMLElement>('.profile-card__timer-pulse');
    let pulse = this.timerPulses.get(playerId) ?? existingPulse ?? undefined;
    if (!pulse) {
      pulse = document.createElement('div');
      pulse.className = 'profile-card__timer-pulse';
      // Insert as the first child so it renders BEHIND everything else
      // inside the circle (z-index:0 + insert order).
      circle.insertBefore(pulse, circle.firstChild);
    }
    gsap.killTweensOf(pulse);
    pulse.style.opacity = '1';
    this.timerPulses.set(playerId, pulse);

    // Ring layer (conic gradient sweep).
    const existingRing = circle.querySelector<HTMLElement>('.profile-card__timer-ring');
    let ring = this.timerRings.get(playerId) ?? existingRing ?? undefined;
    if (!ring) {
      ring = document.createElement('div');
      ring.className = 'profile-card__timer-ring';
      circle.appendChild(ring);
    }
    gsap.killTweensOf(ring);
    ring.style.opacity = '1';
    this.timerRings.set(playerId, ring);

    // Restart any prior tween for this card.
    const prior = this.timerTweens.get(playerId);
    if (prior) prior.kill();

    const total = Math.max(1, endsAt - startedAt);
    // Use server-clock estimate so the tween's startPct is anchored to the
    // server's view of time. The tween itself runs in real seconds (GSAP),
    // so completion lands at server's `endsAt` regardless of clock skew.
    const now = this.serverNow();
    const remainingMs = Math.max(0, endsAt - now);
    const startPct = Math.min(100, Math.max(0, ((now - startedAt) / total) * 100));

    // udgpoker-style: start at current elapsed pct, drive to 100% linearly
    // over remaining time. Each frame interpolates colour, pulse rate, and
    // pulse spread continuously — no threshold flips, no visible "step" in
    // urgency at fixed pcts.
    const proxy = { pct: startPct };
    const ringEl = ring;
    const pulseEl = pulse;
    // Repaint throttle: rewriting the conic-gradient string forces a full
    // re-rasterization of the ring disc, and this tween runs for the WHOLE
    // turn timer (15-30s, sometimes several rings at once in HI_LO). Only
    // rewrite when the fill advanced ≥0.5% — 1.8° of arc, ~7-13 writes/s —
    // visually identical to per-frame writes for a linear fill, at a tenth
    // of the repaint cost. The pulse layer's own CSS keyframe keeps running
    // at full rate; only its rate/spread/colour vars step, imperceptibly.
    let lastAppliedPct = -1;
    const applyFrame = () => {
      const p = proxy.pct;
      if (p - lastAppliedPct < 0.5 && p < 100) return;
      lastAppliedPct = p;
      const t = p / 100;
      // Ring colour: gold (#FFD700) → red (#FF3366) linear lerp.
      const r = 255;
      const g = Math.round(215 - (215 - 51) * t);
      const b = Math.round(0 + 102 * t);
      ringEl.style.background = `conic-gradient(rgb(${r},${g},${b}) 0% ${p}%, rgba(0,0,0,0) ${p}% 100%)`;
      // Pulse: same gold→red on the active colour, rate accelerates from
      // 1000ms (calm) at p=0 to 280ms (frantic) at p=100, spread grows
      // 18px → 28px so urgency feels visceral, not just faster.
      const rateMs = 1000 - 720 * t;
      const spread = 18 + 10 * t;
      pulseEl.style.setProperty('--pulse-rate-ms', `${rateMs.toFixed(0)}ms`);
      pulseEl.style.setProperty('--pulse-spread', `${spread.toFixed(1)}px`);
      pulseEl.style.setProperty('--pulse-color-on', `rgba(${r}, ${g}, ${b}, ${(0.7 + 0.22 * t).toFixed(2)})`);
      pulseEl.style.setProperty('--pulse-color-off', `rgba(${r}, ${g}, ${b}, 0)`);
    };
    applyFrame();

    if (remainingMs <= 0) return;
    const tween = gsap.to(proxy, {
      pct: 100,
      duration: remainingMs / 1000,
      ease: 'none',
      onUpdate: applyFrame,
    });
    this.timerTweens.set(playerId, tween);
  }

  /**
   * Schedule the last-five-seconds audio cues for the active timer. Called
   * once per timer sig change, NOT per ring, so multiple contender rings in
   * HI_LO produce one tick stream, not N. Caller must clear prior ticks
   * (`clearTimerTicks`) before scheduling — handled by setProfileTimer.
   */
  setSpeaking(playerId: string, isSpeaking: boolean): void {
    if (isSpeaking) {
      this.speakingPlayers.add(playerId);
    } else {
      this.speakingPlayers.delete(playerId);
    }
    // A muted opponent never shows the speaking glow.
    const show = isSpeaking && !this.mutedPeers.has(playerId);
    // Update both the small overlay mic (non-local players) and the chunky
    // standalone mic (local player) — only one of the two will exist per card.
    const micEls = this.root.querySelectorAll(
      `[data-player-id="${playerId}"] .profile-card__mic, [data-player-id="${playerId}"] .profile-card__mic-large`,
    );
    micEls.forEach((el) => el.classList.toggle('speaking', show));
  }

  /**
   * Apply the audio.micMode setting — the ONLY way mode changes reach the
   * button (main.ts pushes both settings-screen changes and mic:mode_request
   * round-trips through here). 'open' turns the mic hot with the latched
   * pulse; 'push' mutes it (unless a PTT press is mid-hold). Both edges are
   * idempotent at the VoiceClient, so the round-trip after a tap (which
   * already went hot on pointerdown) is free.
   */
  setMicMode(mode: MicMode): void {
    if (this.micMode === mode) return;
    this.micMode = mode;
    const el = this.root.querySelector<HTMLElement>('.profile-card__mic-large');
    el?.classList.toggle('mic-latched', mode === 'open');
    if (el) this.syncMicAria(el);
    if (mode === 'open') {
      this.emit('ptt:start', undefined as void);
    } else if (!this.isPushToTalk) {
      this.emit('ptt:stop', undefined as void);
    }
  }

  /**
   * Stop transmitting WITHOUT changing the player's mic-mode preference.
   * main.ts calls this on backgrounding, game teardown, and voice-disable so
   * a hot mic never outlives its context; open mic re-arms via
   * reassertMicHot / show() when the game surface returns.
   */
  muteMic(): void {
    this.isPushToTalk = false;
    const el = this.root.querySelector<HTMLElement>('.profile-card__mic-large');
    el?.classList.remove('ptt-active');
    this.emit('ptt:stop', undefined as void);
  }

  /**
   * Re-arm open mic after a muteMic() (background → resume) or a fresh match.
   * No-op in push mode or while the overlay is hidden.
   */
  reassertMicHot(): void {
    if (this.micMode === 'open' && this.isVisible) {
      this.emit('ptt:start', undefined as void);
    }
  }

  private syncMicAria(el: HTMLElement): void {
    const hot = this.isPushToTalk || this.micMode === 'open';
    el.setAttribute('aria-label', hot ? 'Turn mic off' : 'Push to talk');
  }

  /** Reflect locally muting/unmuting an opponent's voice on their speaker icon. */
  setPeerMuted(playerId: string, muted: boolean): void {
    if (muted) this.mutedPeers.add(playerId);
    else this.mutedPeers.delete(playerId);
    const micEls = this.root.querySelectorAll<HTMLElement>(
      `[data-player-id="${playerId}"] .profile-card__mic`,
    );
    micEls.forEach((el) => {
      el.classList.toggle('muted', muted);
      if (muted) el.classList.remove('speaking');
      el.setAttribute('aria-label', muted ? 'Unmute player' : 'Mute player');
      el.innerHTML = muted ? SPEAKER_OFF_ICON_SVG : SPEAKER_ON_ICON_SVG;
    });
  }

  /**
   * v2 entry point: rebuild the profile cards directly from a GameUpdate.
   * Encapsulates the equipped-dice-precedence + showDice-by-phase logic
   * that GameFlowController.updateProfiles previously owned, so v2
   * states can refresh profiles via a single call without re-deriving
   * the snapshot shape.
   *
   * `ctx` carries cross-subsystem data the overlay can't otherwise
   * reach (local player id, local equipped dice, opponent equipped
   * dice lookup, cdn base for thumbnails).
   */
  refreshFromUpdate(
    update: import('@sdl/shared').GameUpdate,
    ctx: {
      localPlayerId: string | null;
      localEquippedDice?: string[];
      getOpponentEquippedDice: (playerId: string) => string[] | null | undefined;
      cdnBase?: string | null;
    },
  ): void {
    this._lastUpdateAnimated = update.animated !== false;
    const players = update.players ?? [];
    const profiles: PlayerProfile[] = players.map((player) => {
      const opponentDice = ctx.getOpponentEquippedDice(player.playerId);
      const equippedDice =
        // Treat an EMPTY array like "not sent" (fall through to the cache) — `??`
        // only catches null/undefined, so a transient `equippedDice: []` on some
        // broadcast would otherwise flip shouldShow off, drop the gear, and make
        // it re-animate back in on the next update (the flicker).
        (player.equippedDice?.length ? player.equippedDice : undefined) ??
        this.equippedDiceCache.get(player.playerId) ??
        (opponentDice?.length ? opponentDice : undefined) ??
        (player.playerId === ctx.localPlayerId ? ctx.localEquippedDice : undefined);

      if (player.equippedDice?.length) {
        this.equippedDiceCache.set(player.playerId, player.equippedDice);
      }

      // Show equipped dice on the profile only for the player(s) about
      // to roll (or currently rolling). HI_LO is many contenders;
      // every other phase has a single currentPlayerId. WAITING /
      // STARTING show everyone's gear (no current roller yet).
      const stateId = update.gameState;
      const isWaitingOrStarting = stateId === 'waiting' || stateId === 'starting';
      const isHiLo = stateId === 'hi_lo';
      const isHiLoContender = isHiLo && (update.hiLoContenders ?? []).includes(player.playerId);
      const isCurrentRoller = !isHiLo && player.playerId === update.currentPlayerId;
      const showDice = isWaitingOrStarting || isHiLoContender || isCurrentRoller;

      return {
        id: player.playerId,
        username: player.username ?? 'Player',
        avatar: (player as { avatar?: string }).avatar,
        balance: player.balance,
        bet: player.bet ?? 0,
        isBanker: player.playerId === update.bankerId,
        isShooter: player.playerId === update.currentPlayerId,
        showDice,
        equippedDice,
        cdnBase: ctx.cdnBase ?? undefined,
      };
    });

    this.setPlayers(profiles, ctx.localPlayerId, update.currentPlayerId, update.bankerId);

    // Drive the "currently betting" activity label from the same
    // snapshot. Server flips player.isBetting when the wallet
    // accumulator changes (drop bills / commit / cancel). Filtering
    // here means the label updates on every game:update naturally —
    // no separate per-state plumbing. For non-betting states the
    // server keeps everyone's isBetting=false, so the filter returns
    // empty and the label hides.
    const bettingPlayers = players
      .filter((p) => (p as { isBetting?: boolean }).isBetting)
      .map((p) => ({
        playerId: p.playerId,
        avatar: (p as { avatar?: string }).avatar,
        username: p.username,
      }));
    this.updateBettingActivity(bettingPlayers);
  }

  /** v2 cache for equipped-dice fallbacks (server doesn't always send them). */
  private equippedDiceCache = new Map<string, string[]>();

  /**
   * Read a player's equipped-dice IDs from the v2 cache. Returns null if
   * we've never seen them in a GameUpdate. Source of truth for callers
   * outside the overlay (e.g. `playShooterAnnouncement`) — the legacy
   * `GameFlowController.playerEquippedDice` map is stale under v2.
   */
  getEquippedDiceFor(playerId: string): string[] | null {
    return this.equippedDiceCache.get(playerId) ?? null;
  }

  /**
   * Diff-based player roster update. Each call:
   *   - removes cards for players no longer in the list
   *   - updates existing cards in place (avatar / name / balance / badges
   *     / shooter class / equipped-dice gear) — preserving timer rings,
   *     pulses, dice-result icons, sidebet chips, and any in-flight CSS
   *     animations attached to the card
   *   - creates new cards for joiners and inserts them in the right slot
   *   - re-slots a card when the bottom-row owner changes (local player
   *     joined, or shooter swapped in shooter-fallback mode)
   *
   * Skips updates entirely when a player's signature (the cheap hash of
   * everything the card renders from) hasn't changed since the last
   * call. This is the common case once the room is full — most updates
   * touch nothing on the cards.
   */
  setPlayers(
    players: PlayerProfile[],
    localPlayerId?: string | null,
    shooterId?: string | null,
    _bankerId?: string | null
  ): void {
    const localId = localPlayerId ?? null;
    this.lastLocalId = localId;
    this.profilesById = new Map(players.map((p) => [p.id, p]));
    const bottomPlayerId = localId ?? shooterId ?? null;

    // Compute target slot for each player.
    const slotByPlayerId = new Map<string, ProfileSlot>();
    const orderedTop = players.filter((p) => p.id !== bottomPlayerId).slice(0, 4);
    orderedTop.forEach((p, i) => {
      slotByPlayerId.set(p.id, i % 2 === 0 ? 'topLeft' : 'topRight');
    });
    const bottom = players.find((p) => p.id === bottomPlayerId);
    if (bottom) slotByPlayerId.set(bottom.id, 'bottomRow');

    // Drop departed players' cards.
    for (const [pid, card] of this.cardsByPlayerId) {
      if (!slotByPlayerId.has(pid)) {
        card.remove();
        this.cardsByPlayerId.delete(pid);
        this.cardSlotByPlayerId.delete(pid);
        this.cardSigByPlayerId.delete(pid);
        this.displayedResults.delete(pid);
        this.displayedSidebets.delete(pid);
        this.displayedBalances.delete(pid);
        this.balanceTweens.get(pid)?.kill();
        this.balanceTweens.delete(pid);
        this.killTimerForPlayer(pid);
      }
    }

    // Render-or-update each player. Iterate in the order they should
    // appear in their slots so newly-inserted cards land in the right
    // visual position.
    const renderList: PlayerProfile[] = bottom ? [bottom, ...orderedTop] : orderedTop;
    for (const player of renderList) {
      const targetSlot = slotByPlayerId.get(player.id)!;
      const sig = this.profileSignature(player, localId);
      const existing = this.cardsByPlayerId.get(player.id);

      if (!existing) {
        const side: 'left' | 'right' = targetSlot === 'topRight' ? 'right' : 'left';
        const card = this.renderPlayer(player, localId, side);
        this.getSlotContainer(targetSlot).appendChild(card);
        this.cardsByPlayerId.set(player.id, card);
        this.cardSlotByPlayerId.set(player.id, targetSlot);
        this.cardSigByPlayerId.set(player.id, sig);
        continue;
      }

      // Re-slot if the bottom-row owner shifted (rare).
      const currentSlot = this.cardSlotByPlayerId.get(player.id);
      if (currentSlot !== targetSlot) {
        const side: 'left' | 'right' = targetSlot === 'topRight' ? 'right' : 'left';
        existing.classList.toggle('side-right', side === 'right');
        this.getSlotContainer(targetSlot).appendChild(existing);
        this.cardSlotByPlayerId.set(player.id, targetSlot);
      }

      if (this.cardSigByPlayerId.get(player.id) === sig) continue;
      this.updateCardInPlace(existing, player, localId);
      this.cardSigByPlayerId.set(player.id, sig);
    }
  }

  /** Latest profile data per player — read on card tap to populate the player card. */
  private profilesById = new Map<string, PlayerProfile>();
  /** Local player id from the last setPlayers — used to skip self-taps. */
  private lastLocalId: string | null = null;
  /** Whether the last update was animated (false on reconnect / state:full).
   *  Drives the equipped-dice slot-swap animation: snap on resume, play live. */
  private _lastUpdateAnimated = true;

  /** Track DOM so updates can diff against the last render instead of rebuilding. */
  private cardsByPlayerId = new Map<string, HTMLElement>();
  private cardSlotByPlayerId = new Map<string, ProfileSlot>();
  private cardSigByPlayerId = new Map<string, string>();

  /**
   * Exact balance currently displayed on each card. formatBalance() is
   * lossy (K/M/B), so the win-bump count-up reads its "from" value from
   * here, not by parsing the DOM text.
   */
  private displayedBalances = new Map<string, number>();
  /** In-flight balance win-bump timelines, keyed by playerId. */
  private balanceTweens = new Map<string, gsap.core.Timeline>();

  /** Raw server balance per player (before the local pending-bet preview), so
   *  setLocalPendingBet can recompute the local card without a server update. */
  private serverBalances = new Map<string, number>();
  /** Local player + their pulled-but-uncommitted bills (client-only preview):
   *  the local card shows `serverBalance - localPendingBet` so the player sees
   *  what's left as they pull notes, before the bet is committed/deducted. */
  private localPlayerId: string | null = null;
  private localPendingBet = 0;
  /** A bet the local player just committed (stack dropped into a zone) that the
   *  server hasn't deducted yet — kept subtracted so the preview doesn't blink
   *  back to the full balance during the commit→broadcast round trip. Cleared
   *  when the authoritative balance moves, or on cancel/reject. */
  private localInFlightBet = 0;

  private getSlotContainer(slot: ProfileSlot): HTMLElement {
    if (slot === 'topLeft') return this.topLeft;
    if (slot === 'topRight') return this.topRight;
    return this.bottomRow;
  }

  /** Card DOM element for a player (for anchoring chat bubbles). Null if unseen. */
  getCardElement(playerId: string): HTMLElement | null {
    return this.cardsByPlayerId.get(playerId) ?? null;
  }

  /** Layout slot a player's card occupies — drives chat-bubble side + tail. */
  getCardSlot(playerId: string): ProfileSlot | null {
    return this.cardSlotByPlayerId.get(playerId) ?? null;
  }

  /** Toggle the unread pulse on the local player's chat badge. */
  setChatUnread(unread: boolean): void {
    const badge = this.root.querySelector<HTMLElement>('.profile-card__chat-badge');
    if (badge) badge.classList.toggle('has-unread', unread);
  }

  /** Avatar URL for a player from the live roster (for chat bubbles / history). */
  getAvatar(playerId: string): string | null {
    return this.profilesById.get(playerId)?.avatar ?? null;
  }

  /**
   * Cheap stringified hash of every PlayerProfile field the card
   * renders from. Used to short-circuit no-op updates.
   */
  private profileSignature(p: PlayerProfile, localId: string | null): string {
    return [
      p.id,
      p.username ?? '',
      p.avatar ?? '',
      p.balance ?? '',
      p.isBanker ? 1 : 0,
      p.isShooter ? 1 : 0,
      p.showDice ? 1 : 0,
      (p.equippedDice ?? []).join('|'),
      p.cdnBase ?? '',
      p.id === localId ? 1 : 0,
    ].join('::');
  }

  /**
   * Update the mutable parts of an already-rendered card in place.
   * Avoids destroying timer rings / pulses / dice-result icons /
   * sidebet chips that live inside the card and would otherwise be
   * wiped by an innerHTML rebuild.
   */
  private updateCardInPlace(
    card: HTMLElement,
    player: PlayerProfile,
    localId: string | null,
  ): void {
    card.classList.toggle('shooter', !!player.isShooter);

    const circle = card.querySelector('.profile-card__circle') as HTMLElement | null;
    if (circle) {
      this.updateCircleAvatar(circle, player);
      this.updateBankBadge(circle, player);
      this.updateDiceGear(circle, player, localId);
    }

    const nameEl = card.querySelector('.profile-card__name');
    if (nameEl) {
      const target = truncateUsername(player.username);
      if (nameEl.textContent !== target) nameEl.textContent = target;
    }

    this.updateBalance(card, player);

    // Speaking indicator on the small overlay mic (non-local cards) —
    // call sites that toggle this still go through speakingPlayers, but
    // refresh the class here in case username/avatar changed.
    if (player.id !== localId) {
      const mic = circle?.querySelector('.profile-card__mic');
      mic?.classList.toggle('speaking', this.speakingPlayers.has(player.id));
    }
  }

  private updateCircleAvatar(circle: HTMLElement, player: PlayerProfile): void {
    const initial = player.username?.trim().charAt(0).toUpperCase() || 'P';
    const existingImg = circle.querySelector('.profile-card__circle-img') as HTMLElement | null;
    const textNode = Array.from(circle.childNodes).find(
      (n) => n.nodeType === Node.TEXT_NODE,
    ) as Text | null;

    if (player.avatar) {
      if (existingImg) {
        const img = existingImg.querySelector('img') as HTMLImageElement | null;
        if (img && img.src !== player.avatar) img.src = player.avatar;
        if (img) img.alt = initial;
      } else {
        const wrap = document.createElement('div');
        wrap.className = 'profile-card__circle-img';
        const img = document.createElement('img');
        img.src = player.avatar;
        img.alt = initial;
        img.onerror = () => {
          wrap.remove();
          circle.insertBefore(document.createTextNode(initial), circle.firstChild);
        };
        wrap.appendChild(img);
        circle.insertBefore(wrap, circle.firstChild);
      }
      if (textNode) textNode.remove();
    } else {
      existingImg?.remove();
      if (textNode) {
        if (textNode.textContent !== initial) textNode.textContent = initial;
      } else {
        circle.insertBefore(document.createTextNode(initial), circle.firstChild);
      }
    }
  }

  private updateBankBadge(circle: HTMLElement, player: PlayerProfile): void {
    const existing = circle.querySelector('.profile-card__bank-badge') as HTMLElement | null;
    if (player.isBanker) {
      if (!existing) {
        const badge = document.createElement('div');
        badge.className = 'profile-card__bank-badge';
        badge.innerHTML = BANK_ICON_SVG;
        circle.appendChild(badge);
      } else if (existing.style.display === 'none') {
        existing.style.display = '';
      }
    } else if (existing) {
      existing.remove();
    }
  }

  private updateDiceGear(circle: HTMLElement, player: PlayerProfile, localId: string | null): void {
    const existing = circle.querySelector('.profile-card__dice-gear') as HTMLElement | null;
    const shouldShow = !!(player.showDice && player.equippedDice?.length && player.cdnBase);

    if (!shouldShow) {
      existing?.remove();
      return;
    }

    const codes = (player.equippedDice ?? []).filter((d): d is string => !!d);
    const cdn = player.cdnBase!;

    let container = existing;
    const firstBuild = !container;
    if (!container) {
      container = document.createElement('div');
      container.className = 'profile-card__dice-gear';
      this.makeGearTappable(container, player.id, player.id === localId);
      circle.appendChild(container);
    }

    // Reconcile per slot index against the codes already on the card. A changed
    // slot animates (scale down → swap → scale up); unchanged slots no-op. Only
    // a live update with a pre-existing gear animates — the first paint and any
    // reconnect / state:full snap (so a late joiner sees the final loadout, no
    // animation). This per-slot diff is the resume-safe centerpiece: every
    // client derives "which slot changed" from state, nothing is transmitted.
    const animate = !firstBuild && this._lastUpdateAnimated;
    const thumbs = Array.from(container.querySelectorAll<HTMLElement>('.profile-card__dice-thumb'));

    // Drop extra thumbs if the loadout shrank.
    for (let i = codes.length; i < thumbs.length; i++) thumbs[i].remove();

    for (let i = 0; i < codes.length; i++) {
      const code = codes[i];
      let thumb = thumbs[i];
      if (!thumb) {
        thumb = document.createElement('div');
        thumb.className = 'profile-card__dice-thumb';
        const img = document.createElement('img');
        img.alt = '';
        const t = thumb;
        img.onerror = () => { t.style.display = 'none'; };
        thumb.appendChild(img);
        thumb.dataset.diceId = '';
        container.appendChild(thumb);
      }
      if (thumb.dataset.diceId === code) continue; // slot unchanged
      const img = thumb.querySelector('img') as HTMLImageElement;
      const src = `${cdn}/dice/${code}/thumb-32.png`;
      thumb.dataset.diceId = code;
      thumb.style.display = '';
      if (animate) {
        this.animateSlotSwap(thumb, img, src);
      } else {
        img.src = src;
      }
    }
  }

  /** Swap one equipped-dice slot thumbnail with a scale-down → swap → scale-up
   *  beat (transform-origin bottom center), so a live change reads as the die
   *  being replaced in the loadout rather than popping. */
  private animateSlotSwap(thumb: HTMLElement, img: HTMLImageElement, newSrc: string): void {
    gsap.killTweensOf(thumb);
    gsap.to(thumb, {
      scale: 0,
      duration: DiceEquippedAnims.slotSwapOutDuration,
      ease: DiceEquippedAnims.slotSwapOutEase,
      transformOrigin: 'bottom center',
      onComplete: () => {
        img.src = newSrc;
        gsap.fromTo(
          thumb,
          { scale: 0 },
          {
            scale: 1,
            duration: DiceEquippedAnims.slotSwapInDuration,
            ease: DiceEquippedAnims.slotSwapInEase,
            transformOrigin: 'bottom center',
          },
        );
      },
    });
  }

  /**
   * Make the local player's equipped-dice gear a tap target that opens the
   * in-game dice library. No-op for opponents (their gear is display-only).
   * `isLocal` is computed by the caller from the per-render localId (the
   * `this.localPlayerId` field isn't set on the v2 setPlayers path — that
   * uses `lastLocalId` — so we thread the id explicitly). The handler
   * downstream gates by phase (active roller + dice at rest); here we only
   * route the intent.
   */
  private makeGearTappable(gearContainer: HTMLElement, playerId: string, isLocal: boolean): void {
    if (!isLocal) return;
    gearContainer.classList.add('profile-card__dice-gear--tappable');
    gearContainer.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.emit('gear:tap', { playerId });
    });
  }

  /** Display balance for a player: the local player sees their own deduction
   *  subtracted (client-only preview) — both notes pulled into the wallet
   *  (`localPendingBet`) and a just-committed bet still in flight to the server
   *  (`localInFlightBet`); everyone else shows the raw server balance. */
  private localBalanceFor(playerId: string, serverBalance: number): number {
    if (playerId === this.localPlayerId) {
      const deduction = this.localPendingBet + this.localInFlightBet;
      if (deduction > 0) return Math.max(0, serverBalance - deduction);
    }
    return serverBalance;
  }

  /** Repaint the local player's balance text from the current server balance
   *  minus the live preview deduction. No-op while a win-bump tween owns it. */
  private refreshLocalBalanceText(playerId: string): void {
    const server = this.serverBalances.get(playerId);
    if (server == null || this.balanceTweens.has(playerId)) return;
    const balEl = this.cardsByPlayerId.get(playerId)?.querySelector('.profile-card__balance') as HTMLElement | null;
    if (!balEl) return;
    const display = this.localBalanceFor(playerId, server);
    this.displayedBalances.set(playerId, display);
    const target = `${formatBalance(display)}`;
    if (balEl.textContent !== target) balEl.textContent = target;
  }

  /**
   * Client-only preview: as the local player pulls notes into the wallet, show
   * their card balance dropping by the accumulated (uncommitted) amount so they
   * see what's left. On commit the amount moves to `localInFlightBet` (see
   * commitLocalPendingBet) so the deduction never blinks back up.
   */
  setLocalPendingBet(playerId: string, amount: number): void {
    this.localPlayerId = playerId;
    this.localPendingBet = Math.max(0, amount);
    this.refreshLocalBalanceText(playerId);
  }

  /**
   * Commit handoff: the moment the player drops the stack into a zone, the
   * wallet zeroes its pending amount — but the server hasn't deducted yet. We
   * move the committed amount into `localInFlightBet` so the card keeps showing
   * the deduction continuously (no flicker back to the full balance). It's
   * cleared the instant the authoritative server balance moves (updateBalance)
   * — at which point the new balance already reflects the deduction, so there's
   * no double-subtraction — or explicitly on cancel/reject (releaseLocalInFlight).
   */
  commitLocalPendingBet(playerId: string, amount: number): void {
    this.localPlayerId = playerId;
    this.localInFlightBet += Math.max(0, amount);
    this.refreshLocalBalanceText(playerId);
  }

  /** Drop an in-flight preview deduction without a server balance change —
   *  used when a create-zone sidebet is cancelled or rejected and the bills
   *  return to the wallet (the wallet re-grows localPendingBet on its own). */
  releaseLocalInFlight(playerId: string): void {
    if (this.localInFlightBet === 0) return;
    this.localInFlightBet = 0;
    this.refreshLocalBalanceText(playerId);
  }

  /**
   * Immediately reconcile a player's displayed balance from an authoritative
   * server value, independent of the full card refresh. The full
   * refreshFromUpdate runs inside the state hooks, which can sit behind a
   * transition animation (e.g. the FADE camera lerp) — so without this the pill
   * lags a beat behind a bet/payout. Called per game:update from the flow
   * controller's bookkeeping so the balance tracks in real time. No-op if the
   * card isn't rendered yet (the first refresh creates it).
   */
  applyServerBalance(playerId: string, balance: number): void {
    const card = this.cardsByPlayerId.get(playerId);
    if (!card) return;
    // Only reconcile DECREASES (bets / losses) and the first balance seen in
    // real time. Skip INCREASES — a win is animated by the flyout count-up
    // (animateBalanceIncrease), so repainting the won balance here too would
    // double-count (count-up adds on top). The full profile refresh reconciles
    // any increase the flyout doesn't drive (e.g. a push refund).
    const prev = this.serverBalances.get(playerId);
    if (prev != null && balance > prev) return;
    this.updateBalance(card, { id: playerId, balance } as PlayerProfile);
  }

  private updateBalance(card: HTMLElement, player: PlayerProfile): void {
    const info = card.querySelector('.profile-card__info') as HTMLElement | null;
    const existing = card.querySelector('.profile-card__balance') as HTMLElement | null;
    if (player.balance == null) {
      existing?.remove();
      this.displayedBalances.delete(player.id);
      this.serverBalances.delete(player.id);
      return;
    }
    // Authoritative balance moved for the local player → a committed bet (or a
    // settlement) has now landed in the server balance. Drop the in-flight
    // preview so we don't double-subtract; the new balance already reflects it.
    const prevServer = this.serverBalances.get(player.id);
    if (
      player.id === this.localPlayerId &&
      this.localInFlightBet > 0 &&
      (prevServer == null || prevServer !== player.balance)
    ) {
      // An authoritative server balance has landed and already reflects the
      // committed bet → drop the optimistic in-flight preview so we don't
      // double-subtract. `prevServer == null` (no balance tracked yet) is
      // included deliberately: updateBalance only runs on a CHANGE, so in solo
      // (constant balance during betting) the FIRST call is the deduct itself,
      // with no prior baseline — the old `!= null` guard skipped the clear and
      // left the first bet showing 2× the stake.
      this.localInFlightBet = 0;
    }
    this.serverBalances.set(player.id, player.balance);
    // A win-bump animation owns the balance text + displayedBalances while it
    // runs; don't fight it. It lands on its own target (the same new balance),
    // and the next signature-changing update reconciles normally once it ends.
    if (this.balanceTweens.has(player.id)) return;
    // Local player: subtract pulled-but-uncommitted bills (client-only preview).
    const display = this.localBalanceFor(player.id, player.balance);
    this.displayedBalances.set(player.id, display);
    const target = `${formatBalance(display)}`;
    if (existing) {
      if (existing.textContent !== target) existing.textContent = target;
      return;
    }
    if (!info) return;
    const balEl = document.createElement('div');
    balEl.className = 'profile-card__balance';
    balEl.textContent = target;
    info.appendChild(balEl);
  }

  /**
   * Win-bump the balance number on a winning player's profile card: count the
   * cash up by `increment` while a 1.1 scale pop rides in parallel (scale up at
   * the start, ease back down as the count finishes). Driven from inside the
   * cash-win flyout (flyoutCashWin) once the last note has landed, using the
   * legacy fixed (amount-independent) `countDuration`.
   *
   * `increment` is the player's total win (notes flying in) — exactly the
   * amount their balance grows by (stake was pre-deducted; payout is gross).
   * The count runs from the currently-displayed balance, which the deferred
   * profile refresh keeps at the pre-win value until the bump owns the text.
   */
  animateBalanceIncrease(
    playerId: string,
    increment: number,
    countDuration: number = BalanceBumpAnims.countDuration,
  ): void {
    if (increment <= 0) return;
    const card =
      this.cardsByPlayerId.get(playerId) ??
      (this.root.querySelector(`[data-player-id="${playerId}"]`) as HTMLElement | null);
    if (!card) return;
    const balEl = card.querySelector('.profile-card__balance') as HTMLElement | null;
    if (!balEl) return;

    const from = this.displayedBalances.get(playerId) ?? 0;
    const to = from + increment;

    this.balanceTweens.get(playerId)?.kill();

    const proxy = { v: from };
    balEl.style.transformOrigin = 'center';
    const tl = gsap.timeline({
      onComplete: () => {
        balEl.textContent = `${formatBalance(to)}`;
        gsap.set(balEl, { clearProps: 'transform,scale' });
        this.displayedBalances.set(playerId, to);
        this.balanceTweens.delete(playerId);
      },
    });
    // Count the cash up over the note-flight duration, starting with the notes.
    tl.to(proxy, {
      v: to,
      duration: countDuration,
      ease: BalanceBumpAnims.countEase,
      onUpdate() {
        balEl.textContent = `${formatBalance(Math.round(proxy.v))}`;
      },
    }, 0);
    // Scale pop in parallel: up at the start, ease back down as the count ends.
    tl.to(balEl, {
      scale: BalanceBumpAnims.peakScale,
      duration: BalanceBumpAnims.scaleUpDuration,
      ease: BalanceBumpAnims.scaleUpEase,
    }, 0);
    tl.to(balEl, {
      scale: 1,
      duration: BalanceBumpAnims.scaleDownDuration,
      ease: BalanceBumpAnims.scaleDownEase,
    }, Math.max(BalanceBumpAnims.scaleUpDuration, countDuration - BalanceBumpAnims.scaleDownDuration));
    this.balanceTweens.set(playerId, tl);
  }

  private killTimerForPlayer(playerId: string): void {
    const tween = this.timerTweens.get(playerId);
    tween?.kill();
    this.timerTweens.delete(playerId);
    this.timerRings.delete(playerId);
    this.timerPulses.delete(playerId);
  }

  private renderPlayer(player: PlayerProfile, localPlayerId: string | null, side: 'left' | 'right'): HTMLElement {
    const isLocal = player.id === localPlayerId;
    const isSpeaking = this.speakingPlayers.has(player.id);
    const initial = player.username?.trim().charAt(0).toUpperCase() || 'P';

    const card = document.createElement('div');
    card.className = 'profile-card';
    card.dataset.playerId = player.id;
    if (isLocal) card.classList.add('local');
    if (player.isShooter) card.classList.add('shooter');
    if (side === 'right') card.classList.add('side-right');

    // ── Main row: avatar + aside ──
    const main = document.createElement('div');
    main.className = 'profile-card__main';

    // ── Circle (avatar) ──
    const circle = document.createElement('div');
    circle.className = 'profile-card__circle';
    if (player.avatar) {
      const imgWrap = document.createElement('div');
      imgWrap.className = 'profile-card__circle-img';
      const img = document.createElement('img');
      img.src = player.avatar;
      img.alt = initial;
      img.onerror = () => { imgWrap.remove(); circle.textContent = initial; };
      imgWrap.appendChild(img);
      circle.appendChild(imgWrap);
    } else {
      circle.textContent = initial;
    }

    // Bank badge
    if (player.isBanker) {
      const badge = document.createElement('div');
      badge.className = 'profile-card__bank-badge';
      badge.innerHTML = BANK_ICON_SVG;
      circle.appendChild(badge);
    }

    if (!isLocal) {
      // Per-opponent speaker icon: doubles as a passive speaking indicator
      // (glows green while they talk) and a tap-to-mute toggle (you stop
      // hearing them locally). The local player gets the chunky PTT button
      // below instead, so we skip this for them.
      const muted = this.mutedPeers.has(player.id);
      const mic = document.createElement('button');
      mic.className = 'profile-card__mic';
      mic.type = 'button';
      mic.setAttribute('aria-label', muted ? 'Unmute player' : 'Mute player');
      if (isSpeaking && !muted) mic.classList.add('speaking');
      if (muted) mic.classList.add('muted');
      mic.innerHTML = muted ? SPEAKER_OFF_ICON_SVG : SPEAKER_ON_ICON_SVG;
      mic.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.emit('peer:mute_toggle', { playerId: player.id });
      });
      circle.appendChild(mic);
    } else {
      // Menu badge — small hamburger at top-right corner of the avatar
      // circle. Settings/lobby/etc. live behind this; emits menu:open and
      // lets whoever owns the menu screen decide what to render.
      const menuBadge = document.createElement('button');
      menuBadge.className = 'profile-card__menu-badge';
      menuBadge.type = 'button';
      menuBadge.setAttribute('aria-label', 'Open menu');
      // Acts on pointerdown + stopPropagation and opens an overlay, so the
      // click-based global delegator can't reliably sound it — play the button
      // press here, and opt the element out of the delegator to avoid a double.
      menuBadge.setAttribute('data-sound', 'none');
      menuBadge.innerHTML = MENU_ICON_SVG;
      menuBadge.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        sound.play(SoundId.UI_BUTTON);
        this.emit('menu:open', undefined as void);
      });
      circle.appendChild(menuBadge);
    }

    // Dice gear
    if (player.showDice && player.equippedDice?.length && player.cdnBase) {
      const gearContainer = document.createElement('div');
      gearContainer.className = 'profile-card__dice-gear';
      player.equippedDice.forEach((diceId) => {
        if (!diceId) return;
        const thumb = document.createElement('div');
        thumb.className = 'profile-card__dice-thumb';
        const img = document.createElement('img');
        img.src = `${player.cdnBase}/dice/${diceId}/thumb-32.png`;
        img.alt = '';
        img.onerror = () => { thumb.style.display = 'none'; };
        thumb.appendChild(img);
        gearContainer.appendChild(thumb);
      });
      this.makeGearTappable(gearContainer, player.id, player.id === localPlayerId);
      circle.appendChild(gearContainer);
    }

    main.appendChild(circle);

    // ── Aside: dice result + sidebets ──
    const aside = document.createElement('div');
    aside.className = 'profile-card__aside';

    // Dice result (top of aside) — populated by updateDiceResults()
    const diceResult = document.createElement('div');
    diceResult.className = 'profile-card__dice-result';
    aside.appendChild(diceResult);

    // Sidebets (bottom of aside)
    const bets = document.createElement('div');
    bets.className = 'profile-card__bets';
    aside.appendChild(bets);

    main.appendChild(aside);
    card.appendChild(main);

    // ── Info (name + balance) — below main ──
    const info = document.createElement('div');
    info.className = 'profile-card__info';

    const nameEl = document.createElement('div');
    nameEl.className = 'profile-card__name';
    nameEl.textContent = truncateUsername(player.username);
    info.appendChild(nameEl);

    if (player.balance != null) {
      const balEl = document.createElement('div');
      balEl.className = 'profile-card__balance';
      balEl.textContent = `${formatBalance(player.balance)}`;
      info.appendChild(balEl);
      this.displayedBalances.set(player.id, player.balance);
    }

    card.appendChild(info);

    if (isLocal) {
      // Chunky push-to-talk mic, stacked above the profile card. Sized for
      // thumb-reach (56dp) and hit-tested on the whole circle so the player
      // doesn't have to aim at the icon. Lives on the card root, not the
      // circle, so it sits above the avatar instead of being a tiny corner
      // overlay. CSS uses `order: -1` to flip it above .profile-card__main
      // without needing insertBefore here.
      const micLarge = document.createElement('button');
      micLarge.className = 'profile-card__mic-large';
      micLarge.type = 'button';
      if (isSpeaking) micLarge.classList.add('speaking');
      if (this.isPushToTalk) micLarge.classList.add('ptt-active');
      if (this.micMode === 'open') micLarge.classList.add('mic-latched');
      micLarge.innerHTML = MIC_ICON_SVG;
      this.syncMicAria(micLarge);
      // Opt out of the global click-sound delegator: its tap fires on the
      // 'click' (pointer-up) = when the mic can go cold, which read as a sound
      // on the wrong (not-hot) edge. We cue on press instead.
      micLarge.dataset.sound = 'none';

      // One unified behavior; the audio.micMode setting IS the latch state:
      // hold = momentary talk (the hot edge stays pointerdown so PTT latency
      // is unchanged); a quick tap flips micMode — ON switches to open mic
      // (mic stays hot until turned off), OFF (any release while open) goes
      // back to hold-to-talk. Mode writes round-trip through the settings
      // store (mic:mode_request → main.ts → setMicMode) so Settings > Audio
      // always shows the live state.
      let pressAt = 0;
      let pressWasOpen = false;
      micLarge.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        sound.play(SoundId.UI_TAP); // cue on press — the hot edge or a deliberate toggle
        pressAt = performance.now();
        pressWasOpen = this.micMode === 'open';
        this.isPushToTalk = true;
        micLarge.classList.add('ptt-active');
        this.syncMicAria(micLarge);
        this.emit('ptt:start', undefined as void); // idempotent when open mic is already hot
      });
      micLarge.addEventListener('pointerup', () => {
        if (!this.isPushToTalk) return;
        this.isPushToTalk = false;
        micLarge.classList.remove('ptt-active');
        const quickTap = performance.now() - pressAt < TAP_LATCH_MS;
        if (pressWasOpen) {
          // Tap toggles open mic OFF; a long hold while open reads as a PTT
          // gesture, which always ends muted. The store round-trip calls
          // setMicMode('push'), which clears the latch and emits ptt:stop.
          this.emit('mic:mode_request', { mode: 'push' });
        } else if (quickTap) {
          // Quick tap on a cold mic switches open mic ON. Already hot from
          // pointerdown; setMicMode('open') adds the latch visuals.
          this.emit('mic:mode_request', { mode: 'open' });
        } else {
          // Plain push-to-talk release.
          this.emit('ptt:stop', undefined as void);
        }
        this.syncMicAria(micLarge);
      });
      // Finger slid off / OS stole the pointer mid-press: never CHANGE mode
      // from an ambiguous gesture. Open mic stays on; a plain PTT hold goes
      // cold, matching the original behavior.
      const cancelPress = () => {
        if (!this.isPushToTalk) return;
        this.isPushToTalk = false;
        micLarge.classList.remove('ptt-active');
        if (this.micMode !== 'open') {
          this.emit('ptt:stop', undefined as void);
          this.syncMicAria(micLarge);
        }
      };
      micLarge.addEventListener('pointerleave', cancelPress);
      micLarge.addEventListener('pointercancel', cancelPress);

      card.appendChild(micLarge);

      // Chat badge — bottom-right corner of the avatar circle, mirroring the
      // menu badge (top-right). Kept off the throw/gesture zone so it doesn't
      // fight dice input. Opens the in-match compose bar.
      const chatBadge = document.createElement('button');
      chatBadge.className = 'profile-card__chat-badge';
      chatBadge.type = 'button';
      chatBadge.setAttribute('aria-label', 'Open match chat');
      chatBadge.innerHTML = CHAT_ICON_SVG;
      chatBadge.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.emit('chat:open', undefined as void);
      });
      circle.appendChild(chatBadge);
    }

    return card;
  }

  /**
   * Animate all currently-displayed dice result icons OUT and clear
   * the cache. Used at HI_LO exit to wipe contender results before
   * the next state's icons land. Fire-and-forget — gsap runs async.
   *
   * Pass `animated=false` to clear instantly with no animation
   * (state-sync / reconnect).
   */
  clearAllDiceResults(animated = true): void {
    const targets = new Map<string, DiceResultUpdate | null>();
    for (const playerId of this.displayedResults.keys()) {
      targets.set(playerId, null);
    }
    if (targets.size === 0) return;
    this.updateDiceResults(targets, animated);
  }

  /**
   * Update dice result icons for all players.
   * @param animated true → flyout from 3D dice position; false → instant placement (reconnect/sync)
   */
  updateDiceResults(results: Map<string, DiceResultUpdate | null>, animated = true): void {
    for (const [playerId, data] of results) {
      const values = data?.values ?? null;
      const key = values ? values.join(',') : '';
      const prev = this.displayedResults.get(playerId) ?? '';

      // No change — skip
      if (key === prev) continue;

      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      if (!card) continue;
      const container = card.querySelector('.profile-card__dice-result') as HTMLElement;
      if (!container) continue;

      // If a flyout is in progress and this is a non-animated (sync) call, skip —
      // let the flyout finish naturally instead of killing it
      const activeTl = this.flyoutTimelines.get(playerId);
      if (activeTl) {
        if (!animated) {
          // Sync call — don't interfere with in-flight animation
          this.displayedResults.set(playerId, key);
          continue;
        }
        // Invoke the timeline's onComplete BEFORE killing so any
        // `waitForAllFlyouts` promise registered against this timeline
        // resolves. GSAP's kill() does NOT fire onComplete, which was
        // hanging HI_LO re-rolls: the second player's previous-round
        // flyout was still in flight when the tie-break re-roll
        // arrived; updateDiceResults killed it (no onComplete) →
        // a parked waitForAllFlyouts never settled → the HI_LO onExit
        // chain stalled → permanent hang.
        const onCompleteCb = activeTl.eventCallback('onComplete') as (() => void) | null;
        activeTl.kill();
        this.flyoutTimelines.delete(playerId);
        onCompleteCb?.();
      }

      // Animate out existing dice
      const existing = container.children;
      const labelForFlyout = animated ? (data?.label ?? null) : null;
      const labelKindForFlyout = animated ? (data?.labelKind ?? null) : null;
      if (existing.length > 0) {
        // Register a pending promise that resolves once the post-animate-out
        // flyout (or instant placement) completes. waitForAllFlyouts() awaits
        // these so callers can't race ahead during the 0.2s animate-out
        // window when flyoutTimelines is empty but a flyout IS coming.
        let resolvePending!: () => void;
        const pending = new Promise<void>((r) => { resolvePending = r; });
        this.pendingFlyoutResolvers.add(pending);
        gsap.to(Array.from(existing), {
          scale: 0,
          duration: 0.2,
          ease: 'back.in(2)',
          stagger: existing.length > 1 ? 0.04 : 0,
          onComplete: () => {
            container.innerHTML = '';
            if (values?.length) {
              this.showNewResults(playerId, container, values, animated ? data?.screenPosition : undefined, labelForFlyout, labelKindForFlyout);
              // Resolve the pending promise when the flyout (or instant
              // placement) actually completes. The flyout timeline (if
              // any) was just registered in flyoutTimelines via
              // showNewResults → flyoutDiceToProfile. Chain off it.
              const flyoutTl = this.flyoutTimelines.get(playerId);
              if (flyoutTl) {
                const existingCb = flyoutTl.eventCallback('onComplete') as (() => void) | null;
                flyoutTl.eventCallback('onComplete', () => {
                  existingCb?.();
                  this.pendingFlyoutResolvers.delete(pending);
                  resolvePending();
                });
              } else {
                // Instant placement (no animated screenPos) → resolve now.
                this.pendingFlyoutResolvers.delete(pending);
                resolvePending();
              }
            } else {
              this.pendingFlyoutResolvers.delete(pending);
              resolvePending();
            }
          },
        });
      } else if (values?.length) {
        this.showNewResults(playerId, container, values, animated ? data?.screenPosition : undefined, labelForFlyout, labelKindForFlyout);
      }

      this.displayedResults.set(playerId, key);
    }
  }

  /** Route to flyout or instant placement based on whether screen position is available */
  private showNewResults(
    playerId: string,
    container: HTMLElement,
    values: number[],
    screenPosition?: { x: number; y: number },
    label?: string | null,
    labelKind?: 'win' | 'point' | 'loss' | null
  ): void {
    if (screenPosition) {
      this.flyoutDiceToProfile(playerId, container, values, screenPosition, label ?? null, labelKind ?? null);
    } else {
      this.placeDiceIcons(container, values);
    }
  }

  /** Instant icon placement (reconnect, state sync, no animation) */
  private placeDiceIcons(container: HTMLElement, values: number[]): void {
    container.innerHTML = '';
    values.slice(0, 3).forEach((value) => {
      const img = document.createElement('img');
      img.src = `/assets/icons/dice/d-${Math.max(1, Math.min(6, Math.round(value)))}.svg`;
      img.alt = String(value);
      container.appendChild(img);
    });
  }

  /**
   * Flyout animation with trails:
   * - A flex group container holds all dice icons (natural spacing via CSS gap)
   * - Group scales in at dice position, arc-flies to profile, fades out
   * - Trail groups follow with delay
   * - Landers pop in at profile container as flyers arrive
   */
  private flyoutDiceToProfile(
    playerId: string,
    container: HTMLElement,
    values: number[],
    screenPos: { x: number; y: number },
    label: string | null = null,
    labelKind: 'win' | 'point' | 'loss' | null = null
  ): void {
    const clamped = values.slice(0, 3);
    const allFlyoutEls: HTMLElement[] = [];
    const tl = gsap.timeline({
      onComplete: () => {
        for (const el of allFlyoutEls) el.remove();
        this.flyoutTimelines.delete(playerId);
      },
    });
    this.flyoutTimelines.set(playerId, tl);

    const containerRect = this._visualRect(container); // zoom-corrected (WebKit)
    const overlayRect = this.flyoutContainer.getBoundingClientRect();

    // worldToScreen gives the spawn point in viewport coords, but the flyout
    // overlay is offset from the viewport (status bar overlays the webview +
    // safe-area inset on iPhone → overlayRect.top ~50px; ~0 on desktop). The
    // DESTINATION already converts to overlay-local (containerRect - overlayRect),
    // so the SOURCE must too — otherwise the flight is off by overlayRect.top,
    // landing above the profile. Invisible on desktop, obvious on iPhone.
    screenPos = { x: screenPos.x - overlayRect.left, y: screenPos.y - overlayRect.top };

    // Helper: create a flyout group with dice icons
    const makeGroup = (isTrail = false): HTMLElement => {
      const group = document.createElement('div');
      group.className = `dice-flyout-group${isTrail ? ' flyout-trail' : ''}`;
      for (const value of clamped) {
        const img = document.createElement('img');
        img.src = `/assets/icons/dice/d-${Math.max(1, Math.min(6, Math.round(value)))}.svg`;
        group.appendChild(img);
      }
      group.style.left = `${screenPos.x}px`;
      group.style.top = `${screenPos.y}px`;
      group.style.transform = 'translate(-50%, -50%) scale(0)';
      this.flyoutContainer.appendChild(group);
      allFlyoutEls.push(group);
      return group;
    };

    // Bezier helper
    const bezier = (a: number, cp: number, b: number, t: number) => {
      const inv = 1 - t;
      return inv * inv * a + 2 * inv * t * cp + t * t * b;
    };

    // Create main flyer group (starts at scale 1 so children can pop in individually)
    const flyer = makeGroup();
    flyer.style.transform = 'translate(-50%, -50%) scale(1)';
    const trails: HTMLElement[] = [];
    for (let t = 0; t < DiceResultAnims.trailCount; t++) {
      trails.push(makeGroup(true));
    }

    // Flight path
    const startX = screenPos.x;
    const startY = screenPos.y;
    const endX = containerRect.left - overlayRect.left + containerRect.width / 2;
    const endY = containerRect.top - overlayRect.top + containerRect.height / 2;
    const cpX = (startX + endX) / 2;
    const cpY = Math.min(startY, endY) - DiceResultAnims.flyArcHeight;

    // Anchored result label — sits above the dice spawn point, scales in
    // a beat after the dice icons, then holds in place while the dice fly
    // away. Faded out at the tail of the timeline (after dice land).
    let labelEl: HTMLElement | null = null;
    if (label) {
      labelEl = document.createElement('div');
      labelEl.className = 'dice-flyout-label';
      labelEl.textContent = label;
      labelEl.style.left = `${startX}px`;
      labelEl.style.top = `${startY - DiceResultAnims.labelOffsetY}px`;
      labelEl.style.transform = 'translate(-50%, -50%) scale(0)';
      labelEl.style.opacity = '0';
      this.flyoutContainer.appendChild(labelEl);
      allFlyoutEls.push(labelEl);
    }

    // Phase 1: Icons pop in staggered at scale 0→1, then GROUP scales up to peak
    const flyerImgs = Array.from(flyer.querySelectorAll('img')) as HTMLElement[];
    flyerImgs.forEach((img, i) => {
      gsap.set(img, { scale: 0 });
      // ONE pop for the whole stack, on the LAST icon's scale-in beat — a
      // per-icon burst read as abrupt with 3 dice (cee-lo), so a single pop
      // caps the stagger instead. Same behavior for craps (2) and HI_LO (1).
      if (i === flyerImgs.length - 1) {
        tl.call(() => { sound.play(SoundId.DICE_RESULT_POP); }, [], i * DiceResultAnims.scaleInStagger);
      }
      tl.to(img, {
        scale: 1,
        duration: DiceResultAnims.scaleInDuration,
        ease: DiceResultAnims.scaleInEase,
      }, i * DiceResultAnims.scaleInStagger);
    });

    // Scale the whole group up to peak after all icons are in
    const groupScaleStart = (clamped.length - 1) * DiceResultAnims.scaleInStagger
      + DiceResultAnims.scaleInDuration * 0.5; // overlap slightly
    tl.to(flyer, {
      duration: DiceResultAnims.scaleInDuration * 0.5,
      ease: 'power2.out',
      onUpdate() {
        const s = gsap.getProperty(flyer, 'scale') as number;
        flyer.style.transform = `translate(-50%, -50%) scale(${s})`;
      },
      scale: DiceResultAnims.scaleInPeak,
    }, groupScaleStart);

    // Phase 2: Arc-fly group to profile (group scales from peak → 1)
    const flyStart = (clamped.length - 1) * DiceResultAnims.scaleInStagger
      + DiceResultAnims.scaleInDuration
      + DiceResultAnims.preFlightPause;

    const flyerProxy = { t: 0 };
    tl.to(flyerProxy, {
      t: 1,
      duration: DiceResultAnims.flyDuration,
      ease: DiceResultAnims.flyEase,
      onUpdate: () => {
        const t = flyerProxy.t;
        const px = bezier(startX, cpX, endX, t);
        const py = bezier(startY, cpY, endY, t);
        const s = DiceResultAnims.scaleInPeak - (DiceResultAnims.scaleInPeak - 1) * t;
        const opacity = t < DiceResultAnims.flyerFadeStart
          ? 1
          : 1 - (t - DiceResultAnims.flyerFadeStart) / (1 - DiceResultAnims.flyerFadeStart);
        flyer.style.left = `${px}px`;
        flyer.style.top = `${py}px`;
        flyer.style.transform = `translate(-50%, -50%) scale(${s})`;
        flyer.style.opacity = String(opacity);
      },
    }, flyStart);

    // Trail groups follow with delay (scale the whole trail group)
    trails.forEach((trail, tIdx) => {
      const trailProxy = { t: 0 };
      tl.to(trailProxy, {
        t: 1,
        duration: DiceResultAnims.flyDuration,
        ease: DiceResultAnims.flyEase,
        onUpdate: () => {
          const t = trailProxy.t;
          const px = bezier(startX, cpX, endX, t);
          const py = bezier(startY, cpY, endY, t);
          const s = (DiceResultAnims.scaleInPeak - (DiceResultAnims.scaleInPeak - 1) * t) * 0.85;
          const baseOpacity = 0.3 - tIdx * 0.1;
          const opacity = t < DiceResultAnims.flyerFadeStart
            ? baseOpacity
            : baseOpacity * (1 - (t - DiceResultAnims.flyerFadeStart) / (1 - DiceResultAnims.flyerFadeStart));
          trail.style.left = `${px}px`;
          trail.style.top = `${py}px`;
          trail.style.transform = `translate(-50%, -50%) scale(${s})`;
          trail.style.opacity = String(Math.max(0, opacity));
        },
      }, flyStart + (tIdx + 1) * DiceResultAnims.trailDelay);
    });

    // Phase 3: Landers pop in at the profile container as flyers begin fading
    // Query container FRESH in case DOM was rebuilt by setPlayers
    const landerStart = (clamped.length - 1) * DiceResultAnims.scaleInStagger
      + DiceResultAnims.scaleInDuration
      + DiceResultAnims.preFlightPause
      + DiceResultAnims.flyDuration * DiceResultAnims.flyerFadeStart;

    tl.call(() => {
      // Re-query container in case DOM was rebuilt by updateProfiles/setPlayers
      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      const freshContainer = card?.querySelector('.profile-card__dice-result') as HTMLElement;
      if (!freshContainer) return;
      freshContainer.innerHTML = '';
      // Profile-card landing pop — distinct from the flyout's DICE_RESULT_POP,
      // one pop for the whole stack (not per icon).
      sound.play(SoundId.PROFILE_RESULT_POP);
      clamped.forEach((value, i) => {
        const img = document.createElement('img');
        img.src = `/assets/icons/dice/d-${Math.max(1, Math.min(6, Math.round(value)))}.svg`;
        img.alt = String(value);
        gsap.set(img, { scale: 0 });
        freshContainer.appendChild(img);
        gsap.to(img, {
          scale: 1,
          duration: DiceResultAnims.landerDuration,
          ease: DiceResultAnims.landerEase,
          delay: i * DiceResultAnims.landerStagger,
        });
      });
      // Update cache so subsequent updateDiceResults calls don't re-trigger
      this.displayedResults.set(playerId, clamped.join(','));
    }, [], landerStart);

    // Label scale-in AFTER every dice icon has popped (sequence: dice pops
    // staggered first, THEN the title) and label fade-out scheduled after
    // the last lander completes plus a small hold so players can read it.
    if (labelEl) {
      const labelStart = (clamped.length - 1) * DiceResultAnims.scaleInStagger
        + DiceResultAnims.scaleInDuration;
      // Outcome stinger fires on the same beat the title pops.
      if (labelKind) {
        const stinger = labelKind === 'win'
          ? SoundId.DICE_RESULT_WIN
          : labelKind === 'loss'
            ? SoundId.DICE_RESULT_LOSS
            : SoundId.DICE_RESULT_POINT;
        tl.call(() => { sound.play(stinger); }, [], labelStart);
      }
      const labelEnter = gsap.timeline();
      labelEnter.to(labelEl, {
        scale: 1,
        opacity: 1,
        duration: DiceResultAnims.labelScaleInDuration,
        ease: DiceResultAnims.labelScaleInEase,
        onUpdate() {
          const s = gsap.getProperty(labelEl as HTMLElement, 'scale') as number;
          (labelEl as HTMLElement).style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      });
      tl.add(labelEnter, labelStart);

      const lastLanderEnd = landerStart
        + (clamped.length - 1) * DiceResultAnims.landerStagger
        + DiceResultAnims.landerDuration;
      tl.to(labelEl, {
        opacity: 0,
        scale: 0.85,
        duration: DiceResultAnims.labelFadeOutDuration,
        ease: DiceResultAnims.labelFadeOutEase,
        onUpdate() {
          const s = gsap.getProperty(labelEl as HTMLElement, 'scale') as number;
          (labelEl as HTMLElement).style.transform = `translate(-50%, -50%) scale(${s})`;
        },
      }, lastLanderEnd + DiceResultAnims.labelHoldAfterLand);
    }
  }

  /**
   * Full player win sequence:
   *   1. Spawn bet icons horizontally stacked at screenPos (back.out zoom-in, staggered)
   *   2. Wait for all icons to finish zoom-in
   *   3. Start money flyout
   *   4. Fade out bet icons (opacity, staggered)
   *
   * @param playerId   Target player's profile to fly to
   * @param totalAmount Total win amount for this player
   * @param betIconUrls Array of bet icon paths (sidebet hex icons or fade icons)
   * @param screenPos  Screen CSS px spawn position (e.g. dice center)
   * @param noteUrl    Small CDN URL for the note image
   */
  /**
   * Update the "Betting" activity label. Shows as a pill under the top
   * profiles when any player has an unplaced pending bet; displays their
   * profile avatars (no names). Hidden when no one is betting.
   *
   * @param players Ordered list of { playerId, avatar?, username? } for
   *   players currently betting. Order should be stable (e.g. by join time).
   */
  updateBettingActivity(players: Array<{ playerId: string; avatar?: string; username?: string }>): void {
    const label = this.root.querySelector('.betting-activity-label') as HTMLElement | null;
    const avatars = label?.querySelector('.betting-activity-label__avatars') as HTMLElement | null;
    if (!label || !avatars) return;

    const nextIds = players.map((p) => p.playerId);
    const prevIds = this.displayedBettingPlayers;

    // Early out: identical list → nothing to do
    if (nextIds.length === prevIds.length && nextIds.every((id, i) => id === prevIds[i])) {
      return;
    }

    this.displayedBettingPlayers = nextIds;

    // Rebuild avatar row. Small list (≤5), DOM churn is negligible.
    avatars.innerHTML = '';
    for (const p of players) {
      const el = document.createElement('div');
      el.className = 'betting-activity-label__avatar';
      if (p.avatar) {
        const img = document.createElement('img');
        img.src = p.avatar;
        img.alt = '';
        el.appendChild(img);
      } else {
        el.textContent = (p.username?.trim().charAt(0).toUpperCase()) || '?';
      }
      avatars.appendChild(el);

      // Scale-in only for newly added ids
      if (!prevIds.includes(p.playerId)) {
        gsap.fromTo(el, { scale: 0 }, { scale: 1, duration: 0.28, ease: 'back.out(1.7)' });
      }
    }

    // Toggle visibility
    if (players.length > 0) {
      label.classList.add('visible');
    } else {
      label.classList.remove('visible');
    }
  }

  /**
   * Update sidebet indicator icons in each profile's `.profile-card__bets` container.
   * Diffs against cache so only newly added icons animate in.
   *
   * @param sidebets Map of playerId → [{ type, count }]
   * @param animated When true, new icons scale-in; when false, set instantly (reconnect/sync).
   */
  updateSidebets(
    sidebets: Map<string, Array<{ type: string; count: number }>>,
    animated = true
  ): void {
    const allIds = new Set<string>([...this.displayedSidebets.keys(), ...sidebets.keys()]);
    for (const playerId of allIds) {
      this.updateSidebetsForPlayer(playerId, sidebets.get(playerId) ?? [], animated);
    }
  }

  private updateSidebetsForPlayer(
    playerId: string,
    entries: Array<{ type: string; count: number }>,
    animated: boolean
  ): void {
    const card = this.root.querySelector(`[data-player-id="${playerId}"]`) as HTMLElement | null;
    if (!card) {
      // 'house' is the solo-grind virtual fade counterparty — it has no profile
      // card by design, so don't warn (it would spam every update).
      if (playerId !== 'house') console.warn('[Sidebets] no card found for player', playerId, 'entries:', entries);
      return;
    }
    const container = card.querySelector('.profile-card__bets') as HTMLElement | null;
    if (!container) {
      console.warn('[Sidebets] no .profile-card__bets container for player', playerId);
      return;
    }
    const prev = this.displayedSidebets.get(playerId) ?? new Map<string, number>();
    const next = new Map<string, number>();
    for (const { type, count } of entries) {
      if (count > 0) next.set(type, (next.get(type) ?? 0) + count);
    }

    // Remove icons no longer present
    for (const type of prev.keys()) {
      if (!next.has(type)) {
        const el = container.querySelector(`[data-sidebet-type="${type}"]`);
        el?.remove();
      }
    }

    // Add new / update existing
    for (const [type, count] of next) {
      let el = container.querySelector(`[data-sidebet-type="${type}"]`) as HTMLElement | null;
      const iconUrl = this.getSidebetIconUrlByType(type);
      if (!el) {
        el = this.createSidebetIcon(type, iconUrl, count);
        container.appendChild(el);
        if (animated) {
          gsap.fromTo(el,
            { scale: 0 },
            {
              scale: 1,
              duration: ProfileSidebetAnims.scaleInDuration,
              ease: ProfileSidebetAnims.scaleInEase,
            }
          );
        } else {
          el.style.transform = 'scale(1)';
        }
      } else {
        // Update count bubble if changed
        const prevCount = prev.get(type) ?? 0;
        if (count !== prevCount) {
          this.setSidebetCount(el, count, animated && count > prevCount);
        }
      }
    }

    if (next.size === 0) {
      this.displayedSidebets.delete(playerId);
    } else {
      this.displayedSidebets.set(playerId, next);
    }
  }

  /** Remove all sidebet icons for a player immediately (used before win flyout). */
  clearSidebets(playerId: string): void {
    const card = this.root.querySelector(`[data-player-id="${playerId}"]`) as HTMLElement | null;
    const container = card?.querySelector('.profile-card__bets') as HTMLElement | null;
    if (container) container.innerHTML = '';
    this.displayedSidebets.delete(playerId);
  }

  private createSidebetIcon(type: string, iconUrl: string, count: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'profile-card__sidebet';
    el.setAttribute('data-sidebet-type', type);
    el.style.transform = 'scale(0)';

    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = type;
    el.appendChild(img);

    const bubble = document.createElement('span');
    bubble.className = 'profile-card__sidebet-count';
    el.appendChild(bubble);

    this.setSidebetCount(el, count, false);
    return el;
  }

  private setSidebetCount(el: HTMLElement, count: number, pulse: boolean): void {
    const bubble = el.querySelector('.profile-card__sidebet-count') as HTMLElement | null;
    if (!bubble) return;
    if (count > 1) {
      bubble.textContent = String(count);
      bubble.style.display = '';
      if (pulse) {
        gsap.fromTo(bubble,
          { scale: ProfileSidebetAnims.countPulseScale },
          {
            scale: 1,
            duration: ProfileSidebetAnims.countPulseDuration,
            ease: ProfileSidebetAnims.countPulseEase,
          }
        );
      }
    } else {
      bubble.style.display = 'none';
    }
  }

  private getSidebetIconUrlByType(type: string): string {
    // Profile-card chips render small (~24-32px) — use the small
    // variant where the label fills more of the circle and stays
    // readable.
    return getSidebetIconUrl(type, 'small');
  }

  async flyoutPlayerWin(
    playerId: string,
    totalAmount: number,
    betIconUrls: string[],
    screenPos: { x: number; y: number },
    noteUrl: string,
    // One sound per settlement, not per winner: when several win flyouts run
    // in parallel the caller marks exactly one as the sound owner (sfx: true)
    // and mutes the rest, so BET_ICON_POP / CASH_COUNT / CASH_LAND don't stack
    // into a same-frame volume spike.
    sfx: boolean = true
  ): Promise<void> {
    // Clear this player's profile sidebet icons — the flyout icons take over visually
    this.clearSidebets(playerId);
    // (Win-flyout audio is driven from flyoutCashWin, synced to the note/count beats.)

    // Step 1: Spawn bet icons stacked horizontally above the spawn point
    const iconEls = await this.spawnBetIconStack(betIconUrls, screenPos, sfx);

    // Step 2: Play money flyout — fade icons out the moment the LAST note starts its flight
    await this.flyoutCashWin(playerId, totalAmount, screenPos, noteUrl, () => {
      this.fadeOutBetIcons(iconEls);
    }, { sfx });
  }

  /** Spawn bet icon stack, await until all have finished scaling in */
  private spawnBetIconStack(
    iconUrls: string[],
    screenPos: { x: number; y: number },
    sfx: boolean = true
  ): Promise<HTMLElement[]> {
    return new Promise((resolve) => {
      if (iconUrls.length === 0) {
        resolve([]);
        return;
      }

      // One pop for the whole stack (a win can carry many bet icons) —
      // fired as the icons start scaling in.
      if (sfx) sound.play(SoundId.BET_ICON_POP);

      // Spawn point into overlay-local coords (icons are children of the flyout
      // overlay). Off by overlayRect.top otherwise — ~0 desktop, ~50px iPhone.
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      screenPos = { x: screenPos.x - overlayRect.left, y: screenPos.y - overlayRect.top };

      const size = CashWinAnims.betIconSize;
      const gap = CashWinAnims.betIconGap;
      const totalWidth = iconUrls.length * size + (iconUrls.length - 1) * gap;
      const startX = screenPos.x - totalWidth / 2 + size / 2;
      const y = screenPos.y - CashWinAnims.betIconYOffset;

      const els: HTMLElement[] = [];
      for (let i = 0; i < iconUrls.length; i++) {
        const img = document.createElement('img');
        img.src = iconUrls[i];
        img.style.position = 'absolute';
        img.style.width = `${size}px`;
        img.style.height = `${size}px`;
        img.style.left = `${startX + i * (size + gap)}px`;
        img.style.top = `${y}px`;
        img.style.transform = 'translate(-50%, -50%) scale(0)';
        img.style.zIndex = '48';
        img.style.filter = 'drop-shadow(0 3px 10px rgba(0, 0, 0, 0.7))';
        img.style.pointerEvents = 'none';
        this.flyoutContainer.appendChild(img);
        els.push(img);
      }

      const tl = gsap.timeline({
        onComplete: () => resolve(els),
      });

      els.forEach((el, i) => {
        tl.to(el, {
          duration: CashWinAnims.betIconInDuration,
          ease: CashWinAnims.betIconInEase,
          onUpdate() {
            const s = gsap.getProperty(el, 'scale') as number;
            el.style.transform = `translate(-50%, -50%) scale(${s})`;
          },
          scale: 1,
        }, i * CashWinAnims.betIconInStagger);
      });
    });
  }

  /** Fade out bet icons with staggered opacity, then remove */
  private fadeOutBetIcons(els: HTMLElement[]): Promise<void> {
    return new Promise((resolve) => {
      if (els.length === 0) { resolve(); return; }
      let remaining = els.length;
      els.forEach((el, i) => {
        gsap.to(el, {
          opacity: 0,
          duration: CashWinAnims.betIconOutDuration,
          delay: i * CashWinAnims.betIconOutStagger,
          ease: 'power2.in',
          onComplete: () => {
            el.remove();
            remaining--;
            if (remaining === 0) resolve();
          },
        });
      });
    });
  }

  /**
   * Fly cash note(s) from a screen position to a player's profile.
   * Used when a player wins a bet/sidebet payout.
   * @param playerId   Target player's profile to fly to
   * @param amount     Win amount (positive = win, shown as +$X)
   * @param screenPos  Screen CSS px spawn position (e.g. dice center)
   * @param noteUrl    Small CDN URL for the note image
   */
  flyoutCashWin(
    playerId: string,
    amount: number,
    screenPos: { x: number; y: number },
    noteUrl: string,
    onLastNoteFlightStart?: () => void,
    opts?: {
      targetOverride?: { x: number; y: number };
      skipBalanceCountUp?: boolean;
      /** Resolve only when the FULL timeline (label linger + float-up +
       *  cleanup) is done. Default resolves early at label pop-in — right for
       *  in-game choreography, but callers that hide the overlay / drop its
       *  z-index boost on resolve (home-screen balance gain) must wait for
       *  the whole thing or the lingering +$X label dives behind the UI. */
      resolveOnComplete?: boolean;
      /** Override the "+$X" label (e.g. "+500" for gold / token flyouts). */
      labelText?: string;
      /** Override the amount-derived note count (small gold / token grants). */
      noteCountOverride?: number;
      /** Lift the "+$X" label up by N times its own height (default 0 = centred
       *  on the target). Used on the home screen so the label clears the gold
       *  pill that sits beside the Stacks pill. Other callers omit it →
       *  unchanged. */
      labelLiftHeights?: number;
      /** Play this flyout's CASH_COUNT / CASH_LAND beats (default true). Set
       *  false on all but one flyout when several run in parallel (multi-winner
       *  settlement) so the sounds fire once per beat, not once per winner. */
      sfx?: boolean;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      // Convert the viewport-space spawn point into overlay-local coords, matching
      // how the destination (and targetOverride) subtract overlayRect. Without this
      // the source is off by overlayRect.top — ~0 on desktop, ~50px on iPhone (status
      // bar overlays the webview + safe-area), so cash landed above the profile.
      screenPos = { x: screenPos.x - overlayRect.left, y: screenPos.y - overlayRect.top };
      let endX: number;
      let endY: number;
      let targetWidth: number;
      if (opts?.targetOverride) {
        // Fly to an arbitrary screen point (e.g. the tutorial's own card avatar)
        // instead of a profile card this overlay manages.
        endX = opts.targetOverride.x - overlayRect.left;
        endY = opts.targetOverride.y - overlayRect.top;
        targetWidth = 64;
      } else {
        const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
        if (!card) { resolve(); return; }
        const circle = card.querySelector('.profile-card__circle') as HTMLElement;
        if (!circle) { resolve(); return; }
        // Target: center of the avatar circle (zoom-corrected for WebKit).
        const circleRect = this._visualRect(circle);
        endX = circleRect.left - overlayRect.left + circleRect.width / 2;
        endY = circleRect.top - overlayRect.top + circleRect.height / 2;
        targetWidth = circleRect.width;
      }

      // Note count reflects the amount (or an explicit override, e.g. 1 token =
      // 1 icon), always floored at 1 and capped at maxNotes — the most icons we
      // ever spawn.
      const rawNoteCount = opts?.noteCountOverride ?? Math.ceil(amount / 500);
      const noteCount = Math.min(CashWinAnims.maxNotes, Math.max(1, rawNoteCount));
      const allEls: HTMLElement[] = [];
      // Time when the last note starts its flight phase (relative to timeline start)
      const lastNoteFlightTime = (noteCount - 1) * CashWinAnims.noteStagger
        + CashWinAnims.scaleInDuration + CashWinAnims.preFlightPause;

      const tl = gsap.timeline({
        onComplete: () => {
          flyoutPool.releaseAll(allEls);
          resolve();
        },
      });

      // Fire callback exactly when the last note starts its flight phase
      if (onLastNoteFlightStart) {
        tl.call(onLastNoteFlightStart, [], lastNoteFlightTime);
      }

      // Notes start scaling in at t=0 — one burst sound for the whole
      // flyout regardless of note count.
      if (opts?.sfx !== false) sound.play(SoundId.CASH_COUNT);

      // Kick the profile balance count-up once the LAST note has finished
      // flying to the card, using the legacy fixed (amount-independent) count
      // duration. lastNoteFlightTime is when the last note STARTS its flight;
      // add flyDuration for when it lands.
      const lastNoteLandTime = lastNoteFlightTime + CashWinAnims.flyDuration;
      if (!opts?.skipBalanceCountUp) {
        tl.call(
          () => this.animateBalanceIncrease(playerId, amount),
          [],
          lastNoteLandTime,
        );
      }
      // Win-flyout SFX intentionally absent — sounds are being authored against
      // recorded footage (Filmora) and will be wired once the files land.

      // Bezier helper
      const bezier = (a: number, cp: number, b: number, t: number) => {
        const inv = 1 - t;
        return inv * inv * a + 2 * inv * t * cp + t * t * b;
      };

      const startX = screenPos.x;
      const startY = screenPos.y;

      for (let n = 0; n < noteCount; n++) {
        // Slight random spread on spawn position
        const offsetX = (Math.random() - 0.5) * 20;
        const offsetY = (Math.random() - 0.5) * 14;
        const spawnX = startX + offsetX;
        const spawnY = startY + offsetY;

        // Arc control point — randomize arc height slightly per note
        const arcJitter = (Math.random() - 0.5) * 30;
        const cpX = (spawnX + endX) / 2 + arcJitter;
        const cpY = Math.min(spawnY, endY) - CashWinAnims.flyArcHeight + arcJitter;

        // Distance-based fade window: fade over the last flyerFadePx PIXELS of
        // the path (flyerFadeStart is the floor for short hops). Path length is
        // the sampled bezier arc — a chord under-measures high arcs.
        let pathLen = 0;
        {
          let prevX = spawnX;
          let prevY = spawnY;
          const SEGS = 8;
          for (let s = 1; s <= SEGS; s++) {
            const st = s / SEGS;
            const sx = bezier(spawnX, cpX, endX, st);
            const sy = bezier(spawnY, cpY, endY, st);
            pathLen += Math.hypot(sx - prevX, sy - prevY);
            prevX = sx;
            prevY = sy;
          }
        }
        const fadeStart = pathLen > 0
          ? Math.max(CashWinAnims.flyerFadeStart, 1 - CashWinAnims.flyerFadePx / pathLen)
          : CashWinAnims.flyerFadeStart;

        // Main note — pooled. Reused across flyouts; we just swap the src to the
        // note image for this denomination (browser-cached by URL).
        const note = flyoutPool.acquire('cash-note', () => {
          const el = document.createElement('img');
          el.className = 'cash-flyout-note';
          return el;
        }) as HTMLImageElement;
        note.src = noteUrl;
        note.style.left = `${spawnX}px`;
        note.style.top = `${spawnY}px`;
        note.style.transform = 'translate(-50%, -50%) scale(0)';
        this.flyoutContainer.appendChild(note);
        allEls.push(note);

        // Trail ghosts — also pooled.
        const trails: HTMLElement[] = [];
        for (let t = 0; t < CashWinAnims.trailCount; t++) {
          const trail = flyoutPool.acquire('cash-trail', () => {
            const el = document.createElement('img');
            el.className = 'cash-flyout-note flyout-trail';
            return el;
          }) as HTMLImageElement;
          trail.src = noteUrl;
          trail.style.left = `${spawnX}px`;
          trail.style.top = `${spawnY}px`;
          trail.style.transform = 'translate(-50%, -50%) scale(0)';
          this.flyoutContainer.appendChild(trail);
          allEls.push(trail);
          trails.push(trail);
        }

        const noteOffset = n * CashWinAnims.noteStagger;

        // Phase 1: Scale in with pop
        tl.to(note, {
          duration: CashWinAnims.scaleInDuration,
          ease: CashWinAnims.scaleInEase,
          onUpdate() {
            const s = gsap.getProperty(note, 'scale') as number;
            note.style.transform = `translate(-50%, -50%) scale(${s})`;
          },
          scale: CashWinAnims.scaleInPeak,
        }, noteOffset);

        // Phase 2: Arc-fly to profile
        const flyStart = noteOffset + CashWinAnims.scaleInDuration + CashWinAnims.preFlightPause;
        const flyProxy = { t: 0 };
        tl.to(flyProxy, {
          t: 1,
          duration: CashWinAnims.flyDuration,
          ease: CashWinAnims.flyEase,
          onUpdate: () => {
            const p = flyProxy.t;
            const px = bezier(spawnX, cpX, endX, p);
            const py = bezier(spawnY, cpY, endY, p);
            const s = CashWinAnims.scaleInPeak - (CashWinAnims.scaleInPeak - 1) * p;
            const opacity = p < fadeStart
              ? 1
              : 1 - (p - fadeStart) / (1 - fadeStart);
            note.style.left = `${px}px`;
            note.style.top = `${py}px`;
            note.style.transform = `translate(-50%, -50%) scale(${s}) rotate(${p * 15 - 7.5}deg)`;
            note.style.opacity = String(opacity);
          },
        }, flyStart);

        // Trail ghosts follow with delay
        trails.forEach((trail, tIdx) => {
          const trailProxy = { t: 0 };
          tl.to(trailProxy, {
            t: 1,
            duration: CashWinAnims.flyDuration,
            ease: CashWinAnims.flyEase,
            onUpdate: () => {
              const p = trailProxy.t;
              const px = bezier(spawnX, cpX, endX, p);
              const py = bezier(spawnY, cpY, endY, p);
              const s = (CashWinAnims.scaleInPeak - (CashWinAnims.scaleInPeak - 1) * p) * 0.8;
              const baseOpacity = 0.25 - tIdx * 0.08;
              const opacity = p < fadeStart
                ? baseOpacity
                : baseOpacity * (1 - (p - fadeStart) / (1 - fadeStart));
              trail.style.left = `${px}px`;
              trail.style.top = `${py}px`;
              trail.style.transform = `translate(-50%, -50%) scale(${s}) rotate(${p * 15 - 7.5}deg)`;
              trail.style.opacity = String(Math.max(0, opacity));
            },
          }, flyStart + (tIdx + 1) * CashWinAnims.trailDelay);
        });
      }

      // Phase 3: Amount text pop-in at profile after last note lands
      const lastNoteEnd = (noteCount - 1) * CashWinAnims.noteStagger
        + CashWinAnims.scaleInDuration
        + CashWinAnims.preFlightPause
        + CashWinAnims.flyDuration * 0.7; // slightly before notes fully land

      const amountEl = document.createElement('div');
      amountEl.className = 'cash-win-amount';
      amountEl.textContent = opts?.labelText ?? `+${formatBalance(amount)}`;
      // Position label to the SIDE of the profile circle (not above — gets clipped at top)
      // Left-side profiles → label on right of circle. Right-side profiles → label on left.
      const profileIsLeftSide = endX < overlayRect.width / 2;
      const sideOffset = targetWidth / 2 + 8;
      const labelX = profileIsLeftSide ? endX + sideOffset : endX - sideOffset;
      amountEl.style.left = `${labelX}px`;
      amountEl.style.top = `${endY}px`;
      const anchorX = profileIsLeftSide ? '0%' : '-100%';
      // Vertical anchor: -50% centres the label on the target. labelLiftHeights
      // shifts it up by whole label-heights on top of that (-50% - N*100%), so
      // a value of 1 lifts it a full height above centre to clear the gold pill.
      const anchorY = `${-(50 + (opts?.labelLiftHeights ?? 0) * 100)}%`;
      amountEl.style.transform = `translate(${anchorX}, ${anchorY}) scale(0)`;
      amountEl.style.opacity = '0';
      // Store anchors for animation onUpdate (so transform stays consistent)
      amountEl.dataset.anchorX = anchorX;
      amountEl.dataset.anchorY = anchorY;
      this.flyoutContainer.appendChild(amountEl);
      allEls.push(amountEl);

      // Cha-ching rides the +$X label pop at the profile.
      if (opts?.sfx !== false) {
        tl.call(() => { sound.play(SoundId.CASH_LAND); }, [], lastNoteEnd);
      }

      tl.to(amountEl, {
        scale: 1,
        opacity: 1,
        duration: CashWinAnims.amountPopDuration,
        ease: CashWinAnims.amountPopEase,
        onUpdate() {
          const s = gsap.getProperty(amountEl, 'scale') as number;
          const o = gsap.getProperty(amountEl, 'opacity') as number;
          const ax = amountEl.dataset.anchorX || '-50%';
          const ay = amountEl.dataset.anchorY || '-50%';
          amountEl.style.transform = `translate(${ax}, ${ay}) scale(${s})`;
          amountEl.style.opacity = String(o);
        },
      }, lastNoteEnd);

      // Float up + fade out
      tl.to(amountEl, {
        opacity: 0,
        y: -16,
        duration: CashWinAnims.amountFadeDuration,
        ease: 'power2.in',
        onUpdate() {
          const o = gsap.getProperty(amountEl, 'opacity') as number;
          amountEl.style.opacity = String(o);
        },
      }, `>+${CashWinAnims.amountLingerDuration}`);

      // Resolve the awaited promise the MOMENT the cash has landed and the
      // +$X label has popped — NOT after its linger + float-up — so the roll
      // state's resolution choreography (handoff / camera lerp) isn't gated on
      // the label's linger. The balance count-up is driven separately, from the
      // first-note flight callback above, so it's already mid-flight by now. The
      // timeline keeps running for the label linger/float and element cleanup
      // (onComplete handles removal). With resolveOnComplete, skip the early
      // resolve — the timeline's onComplete (above) resolves after cleanup.
      if (!opts?.resolveOnComplete) {
        tl.call(() => resolve(), [], lastNoteEnd + CashWinAnims.amountPopDuration);
      }
    });
  }

  /**
   * Fly equipped dice thumbnails from screen center to a player's profile.
   * Used to announce whose turn it is to shoot.
   */
  flyoutDiceAnnouncement(
    playerId: string,
    diceIds: string[],
    cdnBase: string
  ): Promise<void> {
    return new Promise((resolve) => {
      const card = this.root.querySelector(`[data-player-id="${playerId}"]`);
      if (!card) { resolve(); return; }

      const circle = card.querySelector('.profile-card__circle') as HTMLElement;
      if (!circle) { resolve(); return; }

      const circleRect = this._visualRect(circle); // zoom-corrected (WebKit)
      const overlayRect = this.flyoutContainer.getBoundingClientRect();
      const isRightSide = card.classList.contains('side-right');

      // Target thumb slot size matches the profile-card__dice-thumb (18px)
      // with the dice-gear container scale (0.85 opponents, 1 local).
      const isLocal = card.classList.contains('local');
      const gearScale = isLocal ? 1 : 0.85;
      const THUMB_SIZE = 18 * gearScale;

      // Compute target positions for each of the three thumb slots.
      // CSS (see .profile-card__dice-gear / __dice-thumb):
      //   dice-gear anchors at circle's bottom-left (bottom:0, left:-8) or
      //   bottom-right for side-right profiles.
      //   Thumb slots within the gear:
      //     slot 0: (left:0, bottom:0)
      //     slot 1: (left:5, bottom:9)
      //     slot 2: (left:10, bottom:-4)
      // Translate those into overlay-space pixel coordinates.
      const GEAR_OFFSET = 8; // matches CSS left:-8 / right:-8
      const THUMB_SLOTS_LOCAL = [
        { x: 0, y: 0 },
        { x: 5, y: 9 },
        { x: 10, y: -4 },
      ];

      const circleLeftInOverlay = circleRect.left - overlayRect.left;
      const circleTopInOverlay = circleRect.top - overlayRect.top;
      // Anchor: bottom-left (or bottom-right mirrored) of the circle.
      const anchorX = isRightSide
        ? circleLeftInOverlay + circleRect.width + GEAR_OFFSET
        : circleLeftInOverlay - GEAR_OFFSET;
      const anchorY = circleTopInOverlay + circleRect.height;

      const targetPositions = THUMB_SLOTS_LOCAL.map((slot) => ({
        // Slot x offsets mirror on side-right; y (from bottom) is unchanged.
        x: anchorX + (isRightSide ? -slot.x - THUMB_SIZE : slot.x) + THUMB_SIZE / 2,
        y: anchorY - slot.y - THUMB_SIZE / 2,
      }));

      // Spawn from center of screen
      const startX = overlayRect.width / 2;
      const startY = overlayRect.height / 2;

      const clamped = diceIds.slice(0, 3);
      const POP_SIZE = DiceEquippedAnims.popSize;
      const POP_GAP = DiceEquippedAnims.popGap;

      const popOffsets = clamped.map((_, i) => {
        const total = clamped.length;
        const center = (total - 1) / 2;
        return (i - center) * (POP_SIZE + POP_GAP);
      });

      const imgs: HTMLImageElement[] = [];
      const perImgState: Array<{
        startX: number;
        startY: number;
        endX: number;
        endY: number;
      }> = [];

      for (let i = 0; i < clamped.length; i++) {
        const img = document.createElement('img');
        img.src = `${cdnBase}/dice/${clamped[i]}/thumb-32.png`;
        img.style.position = 'absolute';
        img.style.left = '0px';
        img.style.top = '0px';
        img.style.width = `${POP_SIZE}px`;
        img.style.height = `${POP_SIZE}px`;
        img.style.objectFit = 'contain';
        img.style.filter = 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.6))';
        img.style.opacity = '0';
        img.style.zIndex = '47';
        img.style.pointerEvents = 'none';
        img.style.willChange = 'transform, opacity';
        img.draggable = false;
        this.flyoutContainer.appendChild(img);
        imgs.push(img);

        const sx = startX + popOffsets[i];
        const sy = startY;
        const tgt = targetPositions[i] ?? targetPositions[targetPositions.length - 1];
        perImgState.push({ startX: sx, startY: sy, endX: tgt.x, endY: tgt.y });

        img.style.transform = `translate(${sx - POP_SIZE / 2}px, ${sy - POP_SIZE / 2}px) scale(0)`;
      }

      const tl = gsap.timeline({
        onComplete: () => {
          for (const img of imgs) img.remove();
          resolve();
        },
      });

      // Phase 1: pop in at screen center (staggered scale + fade in)
      imgs.forEach((img, i) => {
        const state = perImgState[i];
        const popProxy = { scale: 0, opacity: 0 };
        tl.to(popProxy, {
          scale: 1,
          opacity: 1,
          duration: DiceEquippedAnims.popInDuration,
          ease: DiceEquippedAnims.popInEase,
          onUpdate: () => {
            const s = popProxy.scale;
            img.style.opacity = String(popProxy.opacity);
            img.style.transform = `translate(${state.startX - POP_SIZE / 2}px, ${state.startY - POP_SIZE / 2}px) scale(${s})`;
          },
        }, DiceEquippedAnims.popInBaseDelay + i * DiceEquippedAnims.popInStagger);
      });

      // Phase 2: straight-line fly to each icon's thumb-slot target.
      const finalScale = THUMB_SIZE / POP_SIZE;

      imgs.forEach((img, i) => {
        const state = perImgState[i];
        const proxy = { t: 0 };
        tl.to(proxy, {
          t: 1,
          duration: DiceEquippedAnims.flyDuration,
          ease: DiceEquippedAnims.flyEase,
          onUpdate: () => {
            const p = proxy.t;
            const px = state.startX + (state.endX - state.startX) * p;
            const py = state.startY + (state.endY - state.startY) * p;
            const s = 1 + (finalScale - 1) * p;
            img.style.transform = `translate(${px - POP_SIZE / 2}px, ${py - POP_SIZE / 2}px) scale(${s})`;
          },
        }, DiceEquippedAnims.flyStart);
      });
    });
  }

  getVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Clear all per-game state on match exit WITHOUT destroying the overlay.
   * The overlay is an app-lifetime singleton — home-screen cash flyouts
   * (daily bonus, connect bonus) reuse it between games, and its event
   * wiring (menu:open, profile:tap) is done once in main(). setPlayers([])
   * drops every card plus its per-player caches, tweens and timer rings.
   */
  resetForGameExit(): void {
    this.hide();
    this.setFlyoutAboveUI(false);
    this.setPlayers([], null);
    this.equippedDiceCache.clear();
    this.speakingPlayers.clear();
    // Through the primitive, not a direct cache write: the pill's 'visible'
    // class must come off too, or the next home cash flyout (which re-shows
    // this overlay) renders a leaked BETTING pill over the main screen.
    this.updateBettingActivity([]);
    this.lastTimerSig = null;
  }

  destroy(): void {
    this.root.remove();
    this.removeAllListeners();
  }
}
